import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  getRapportiniByCantiereId, 
  createRapportino, 
  updateRapportino, 
  deleteRapportino,
  duplicateRapportino,
  getAllUsers,
  getMezziAttrezzature,
  getMaterialiCostoVariabile,
  getMaterialiCostoFisso,
  getLavorazioni,
  uploadRapportinoFile
} from '../../firebase';
import { useClientSideWorkHoursSync } from '../../hooks/useClientSideWorkHoursSync';
import { useScrollPreservation } from '../../hooks/useScrollPreservation';
import { useAuth } from '../../contexts/AuthContext';
import { useCollapsibleSections } from '../../hooks/useCollapsibleSections'; // NUOVO
import { CollapsibleFormSection, SectionControls } from '../shared/CollapsibleFormSection'; // NUOVO
import '../Admin/Cantieri/RapportiniManagement.css'; // CSS esterno unificato
import {
  MezziAutocomplete,
  MaterialiVariabiliAutocomplete,
  MaterialiFissiAutocomplete,
  LavorazioniAutocomplete
} from '../Admin/Cantieri/AutocompleteComponents';
import AutoPersonaleSectionResponsabili, { syncPendingManualHours } from '../Admin/Cantieri/AutoPersonaleSectionResponsabili';
import SettingsDropdownPortal from '../Admin/Cantieri/SettingsDropdownPortal';
import { syncRapportiniAfterWorkHoursChange } from '../../services/clientSideRapportiniSync';
import { triggerBatchDistribution } from '../../services/proportionalHoursDistribution';

import SyncStatusBadge from '../shared/SyncStatusBadge';
import DatePickerModal from '../shared/DatePickerModal';

const formatEuro = (valore) => {
  if (typeof valore !== 'number') return '';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(valore);
};

// Componente di ricerca con suggerimenti
const SearchWithSuggestions = ({ 
  placeholder, 
  value, 
  onChange, 
  suggestions, 
  onSelectSuggestion, 
  getSuggestionText,
  renderSuggestion,
  maxSuggestions = 5 
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  const filteredSuggestions = suggestions
    .filter(item => {
      const text = getSuggestionText(item).toLowerCase();
      return text.includes(value.toLowerCase());
    })
    .slice(0, maxSuggestions);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setShowSuggestions(inputValue.length > 0);
    setActiveSuggestion(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    onSelectSuggestion(suggestion);
    setShowSuggestions(false);
    setActiveSuggestion(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestion >= 0 && filteredSuggestions[activeSuggestion]) {
        handleSuggestionClick(filteredSuggestions[activeSuggestion]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  };

  return (
    <div className="search-with-suggestions">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="search-input"
      />
      
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setActiveSuggestion(index)}
            >
              {renderSuggestion ? renderSuggestion(suggestion, value) : getSuggestionText(suggestion)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DistributionStatusIndicator = ({ rapportino }) => {
  if (!rapportino) return null;
  const hasDistributedHours = rapportino.personalePresente?.some(p => p.distributedHours === true);
  const distributionMetadata = rapportino.distributionMetadata;
  if (!hasDistributedHours && !distributionMetadata) return null;
  return (
    <div className="distribution-status-badge">
      <span className="badge badge-info">📊 Ore distribuite</span>
      {distributionMetadata && (
        <small>
          {distributionMetadata.affectedPersonIds?.length || 0} persone 
          su {distributionMetadata.numCantieri || 0} cantieri
        </small>
      )}
    </div>
  );
};

// Componente principale per responsabili (SENZA COSTI)
const ResponsabileRapportiniManagement = ({ cantiere, onBack }) => {

  const handleNewRapportino = () => {
    const oggi = new Date().toISOString().split('T')[0];

    setEditingRapportino(null);
    setSelectedRapportino(null);
    setFormData({
      data: oggi,
      redattoDa: currentUser?.uid || '',
      personalePresente: [],
      mezziUtilizzati: [],
      materialiVariabili: [],
      materialiFissi: [],
      lavorazioni: [],
      ricavi: [],
      noteVarie: '',
      allegati: []
    });
    setShowForm(true);

    console.log("🆕 Nuovo rapportino inizializzato.");
  };
  const { currentUser } = useAuth();
  
  // Stati principali
  const [rapportini, setRapportini] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRapportino, setEditingRapportino] = useState(null);
  const [selectedRapportino, setSelectedRapportino] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRapportini, setFilteredRapportini] = useState([]);
  const [openSettingsDropdown, setOpenSettingsDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  // 🆕 AGGIUNGI QUESTI TRE STATI:
const [showCopyMezziModal, setShowCopyMezziModal] = useState(false);
const [availableDatesForCopy, setAvailableDatesForCopy] = useState([]);
const [selectedDateForCopy, setSelectedDateForCopy] = useState('');


  // Aggiungi questi stati insieme agli altri useState:
const [showDateModal, setShowDateModal] = useState(false);
const [rapportinoToDuplicate, setRapportinoToDuplicate] = useState(null);
  
  // Stati per il form rapido
  const [selectedSection, setSelectedSection] = useState('informazioni-base');
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    redattoDa: currentUser?.uid || '',
    personalePresente: [],
    mezziUtilizzati: [],
    materialiVariabili: [],
    materialiFissi: [],
    lavorazioni: [],
    // 🔴 RIMUOVI COMPLETAMENTE: ricavi: [], // Nascosto per responsabili
    noteVarie: '',
    allegati: []
  });
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Dati di supporto
  const [users, setUsers] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [materialiVariabili, setMaterialiVariabili] = useState([]);
  const [materialiFissi, setMaterialiFissi] = useState([]);
  const [lavorazioni, setLavorazioni] = useState([]);
  
  // Stato per nomi utenti
  const [userNames, setUserNames] = useState({});
  
  // Stati per le ricerche
  const [searchPersonale, setSearchPersonale] = useState('');
  const [searchMezzi, setSearchMezzi] = useState('');
  const [searchMatVariabili, setSearchMatVariabili] = useState('');
  const [searchMatFissi, setSearchMatFissi] = useState('');
  const [searchLavorazioni, setSearchLavorazioni] = useState('');

   // Sezioni specifiche per responsabili (SENZA ricavi)  
   const formSections = [
    'informazioni-base',
    'personale-presente', 
    'mezzi-utilizzati',
    'materiali-fissi',
    'allegati',
    'note-varie'
  ];

  // Hook per sezioni collassabili
  const { 
    toggleSection, 
    isSectionCollapsed, 
    expandAllSections, 
    collapseAllSections,
    collapsedSections 
  } = useCollapsibleSections(formSections);

  // Determina gli utenti attivi dai rapportini esistenti
  const activeUsers = useMemo(() => {
    const users = new Set();
    
    rapportini.forEach(rapportino => {
      if (rapportino.personalePresente) {
        rapportino.personalePresente.forEach(persona => {
          if (persona.personaId) {
            users.add(persona.personaId);
          }
        });
      }
    });
    
    return Array.from(users);
  }, [rapportini]);


  // Caricamento dati iniziale
  useEffect(() => {
    loadRapportini();
    loadSupportData();
  }, [cantiere.id]);

  // useEffect per il filtro rapportini
  useEffect(() => {
    const filtered = filterRapportini(rapportini, searchTerm);
    setFilteredRapportini(filtered);
  }, [rapportini, searchTerm]);

  // useEffect per rilevare mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Caricamento rapportini
  const loadRapportini = async () => {
    try {
      const data = await getRapportiniByCantiereId(cantiere.id);
      console.log('Raw rapportini data:', data);
      
      const processedData = data.map(rapportino => ({
        ...rapportino,
      }));
      
      const sortedData = processedData.sort((a, b) => {
        try {
          const dateA = new Date(a.data);
          const dateB = new Date(b.data);
          return dateB - dateA;
        } catch (error) {
          console.error('Errore nell\'ordinamento date:', error);
          return 0;
        }
      });
      
      setRapportini(sortedData);
      
      if (sortedData.length > 0 && !selectedRapportino) {
        handleSelectRapportino(sortedData[0]);
      }
    } catch (error) {
      console.error('Errore nel caricamento rapportini:', error);
      showNotification('Errore nel caricamento dei rapportini', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Caricamento dati di supporto
  const loadSupportData = async () => {
    try {
      const [usersData, mezziData, matVariabiliData, matFissiData, lavorazioniData] = await Promise.all([
        getAllUsers(),
        getMezziAttrezzature(),
        getMaterialiCostoVariabile(),
        getMaterialiCostoFisso(),
        getLavorazioni()
      ]);
      
      setUsers(usersData);
      setMezzi(mezziData);
      setMaterialiVariabili(matVariabiliData);
      setMaterialiFissi(matFissiData);
      setLavorazioni(lavorazioniData);
      
      await loadUserNames(usersData);
    } catch (error) {
      console.error('Errore nel caricamento dati di supporto:', error);
    }
  };
  
  // 🆕 NUOVO: Sincronizzazione per rapportino specifico
  const handleSyncRapportino = async (rapportino) => {
    if (!rapportino || !rapportino.personalePresente) return;
    
    console.log('🔄 [RESPONSABILE] Sincronizzazione rapportino specifico:', rapportino.id);
    setSyncStatus('syncing');
    
    try {
      const dateStr = rapportino.data instanceof Date ? 
        rapportino.data.toISOString().split('T')[0] : 
        (typeof rapportino.data === 'string' ? rapportino.data : new Date(rapportino.data).toISOString().split('T')[0]);
      
      for (const persona of rapportino.personalePresente) {
        if (persona.personaId) {
          await syncRapportiniAfterWorkHoursChange(persona.personaId, dateStr);
        }
      }
      
      setSyncStatus('success');
      await loadRapportini();
      
      showNotification('🔄 Rapportino sincronizzato con successo!', 'success');
      setTimeout(() => setSyncStatus(null), 3000);
      
    } catch (error) {
      console.error('❌ [RESPONSABILE] Errore sincronizzazione rapportino:', error);
      setSyncStatus('error');
      showNotification('❌ Errore sincronizzazione rapportino', 'error');
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };

  // Funzione per caricare i nomi utenti
  const loadUserNames = async (usersData) => {
    try {
      const names = {};
      for (const user of usersData) {
        if (user.nome && user.cognome) {
          names[user.id] = `${user.nome} ${user.cognome}`;
        } else {
          names[user.id] = user.email || 'Utente sconosciuto';
        }
      }
      setUserNames(names);
    } catch (error) {
      console.error('Errore nel caricamento nomi utenti:', error);
    }
  };

// 🆕 FUNZIONE PER OTTENERE DATE DISPONIBILI CON MEZZI
const getAvailableDatesWithMezzi = useCallback(() => {
  if (!rapportini || rapportini.length === 0) return [];
  
  // Filtra solo rapportini con mezzi utilizzati
  const rapportiniConMezzi = rapportini.filter(r => 
    r.mezziUtilizzati && 
    r.mezziUtilizzati.length > 0 &&
    r.data !== formData.data // Escludi la data corrente
  );
  
  // Ordina per data decrescente (più recenti prima)
  return rapportiniConMezzi
    .sort((a, b) => {
      const dateA = new Date(timestampToDateInput(a.data));
      const dateB = new Date(timestampToDateInput(b.data));
      return dateB - dateA;
    })
    .map(r => ({
      data: timestampToDateInput(r.data),
      id: r.id,
      mezziUtilizzati: r.mezziUtilizzati,
      displayDate: new Date(timestampToDateInput(r.data)).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      numeroMezzi: r.mezziUtilizzati.length
    }));
}, [rapportini, formData.data]);

// 🆕 FUNZIONE PER APRIRE IL MODAL DI COPIA MEZZI
const handleOpenCopyMezziModal = () => {
  const dates = getAvailableDatesWithMezzi();
  
  if (dates.length === 0) {
    showNotification('Non ci sono rapportini precedenti con mezzi da copiare', 'warning');
    return;
  }
  
  setAvailableDatesForCopy(dates);
  setSelectedDateForCopy('');
  setShowCopyMezziModal(true);
};

// 🆕 FUNZIONE PER COPIARE DAL GIORNO PRECEDENTE
const handleCopyFromPreviousDay = () => {
  if (!formData.data) {
    showNotification('Imposta prima la data del rapportino', 'warning');
    return;
  }

  // Calcola il giorno precedente
  const currentDate = new Date(formData.data);
  currentDate.setDate(currentDate.getDate() - 1);
  const previousDay = currentDate.toISOString().split('T')[0];

  // Cerca il rapportino del giorno precedente
  const rapportinoPrecedente = availableDatesForCopy.find(r => r.data === previousDay);

  if (!rapportinoPrecedente) {
    showNotification(
      `Non è stato trovato un rapportino con mezzi per il ${currentDate.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}`,
      'warning'
    );
    return;
  }

  // Esegui la copia
  copyMezziFromSelectedDate(rapportinoPrecedente);
};

// 🆕 FUNZIONE HELPER PER COPIARE I MEZZI
const copyMezziFromSelectedDate = (rapportinoSelected) => {
  if (!rapportinoSelected || !rapportinoSelected.mezziUtilizzati) {
    showNotification('Rapportino non trovato o senza mezzi', 'error');
    return;
  }
  
  // Mezzi già presenti nel rapportino corrente
  const mezziAttuali = formData.mezziUtilizzati || [];
  const mezziAttualiIds = new Set(mezziAttuali.map(m => m.mezzoId));
  
  // Filtra solo i mezzi che non sono già presenti
  const mezziDaCopiare = rapportinoSelected.mezziUtilizzati.filter(
    mezzo => !mezziAttualiIds.has(mezzo.mezzoId)
  );
  
  if (mezziDaCopiare.length === 0) {
    showNotification('Tutti i mezzi del rapportino selezionato sono già presenti', 'info');
    setShowCopyMezziModal(false);
    return;
  }
  
  // Copia i mezzi con i loro dati
  const nuoviMezzi = mezziDaCopiare.map(mezzo => ({
    mezzoId: mezzo.mezzoId,
    nomeMezzo: mezzo.nomeMezzo,
    targa: mezzo.targa || '',
    ore: mezzo.ore || mezzo.oreLavorate || 0,
    oreLavorate: mezzo.ore || mezzo.oreLavorate || 0,
    costoOrario: mezzo.costoOrario || 0,
    totale: (mezzo.ore || mezzo.oreLavorate || 0) * (mezzo.costoOrario || 0)
  }));
  
  // Aggiorna formData con i nuovi mezzi
  const mezziAggiornati = [...mezziAttuali, ...nuoviMezzi];
  
  setFormData(prev => ({
    ...prev,
    mezziUtilizzati: mezziAggiornati
  }));
  
  // Chiudi il modal
  setShowCopyMezziModal(false);
  
  // Mostra notifica di successo
  const displayDate = new Date(rapportinoSelected.data).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  showNotification(
    `✅ Copiati ${nuoviMezzi.length} mezzi dal rapportino del ${displayDate}`,
    'success'
  );
  
  // Se siamo in modalità editing, aggiorna anche il database
  if (editingRapportino) {
    handleQuickUpdate('mezziUtilizzati', mezziAggiornati);
  }
};

// 🆕 FUNZIONE PER COPIARE I MEZZI DA UNA DATA SELEZIONATA MANUALMENTE DAL CALENDARIO
const handleCopyMezziFromDate = () => {
  if (!selectedDateForCopy) {
    showNotification('Seleziona una data dal calendario', 'warning');
    return;
  }
  
  // Cerca se esiste un rapportino per la data selezionata
  const rapportinoSelected = availableDatesForCopy.find(r => r.data === selectedDateForCopy);
  
  if (!rapportinoSelected) {
    // La data selezionata non ha un rapportino con mezzi
    const selectedDateFormatted = new Date(selectedDateForCopy).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    showNotification(
      `❌ Non esiste un rapportino con mezzi per il ${selectedDateFormatted}`,
      'error'
    );
    return;
  }
  
  // Usa la funzione helper per copiare i mezzi
  copyMezziFromSelectedDate(rapportinoSelected);
};

  // Funzione helper per ottenere il nome utente
  const getUserName = (userId) => {
    if (!userId) return 'Utente sconosciuto';
    
    const user = users.find(u => u.id === userId);
    if (user) {
      if (user.nome && user.cognome) {
        return `${user.nome} ${user.cognome}`;
      }
      return user.email || 'Utente sconosciuto';
    }
    
    return userNames[userId] || 'Caricamento...';
  };

  // Funzione helper per ottenere il nome persona dal personalePresente
  const getPersonaName = (persona) => {
    if (!persona) return 'Persona sconosciuta';
    
    if (persona.nome && persona.cognome) {
      return `${persona.nome} ${persona.cognome}`;
    }
    
    if (persona.personaId) {
      const user = users.find(u => u.id === persona.personaId);
      if (user) {
        if (user.nome && user.cognome) {
          return `${user.nome} ${user.cognome}`;
        }
        return user.email || 'Utente sconosciuto';
      }
    }
    
    if (persona.userId) {
      return getUserName(persona.userId);
    }
    
    return 'Persona sconosciuta';
  };

  // Funzione helper per formattare la data del rapportino
  const formatRapportinoDate = (dataField) => {
    if (!dataField) return 'Data non disponibile';
    
    try {
      let date;
      
      if (dataField && typeof dataField.toDate === 'function') {
        date = dataField.toDate();
      }
      else if (dataField && typeof dataField === 'object' && dataField.seconds) {
        date = new Date(dataField.seconds * 1000);
      }
      else if (dataField instanceof Date) {
        date = dataField;
      }
      else if (typeof dataField === 'string') {
        date = new Date(dataField);
      }
      else if (typeof dataField === 'number') {
        date = new Date(dataField);
      }
      else if (typeof dataField === 'object') {
        date = new Date(dataField.toString());
      }
      else {
        date = new Date(dataField);
      }
      
      if (date && !isNaN(date.getTime())) {
        return date.toLocaleDateString('it-IT');
      } else {
        return 'Data non valida';
      }
    } catch (error) {
      console.error('Errore nella formattazione data rapportino:', error, 'Valore:', dataField);
      return 'Data non valida';
    }
  };

  // Funzione helper per convertire timestamp Firebase in stringa data per input
  const timestampToDateInput = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      let date;
      
      if (timestamp && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } 
      else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } 
      else if (timestamp instanceof Date) {
        date = timestamp;
      } 
      else if (typeof timestamp === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
          return timestamp;
        }
        date = new Date(timestamp);
      } 
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } 
      else if (typeof timestamp === 'object') {
        date = new Date(timestamp.toString());
      } 
      else {
        date = new Date(timestamp);
      }
      
      if (date && !isNaN(date.getTime())) {
        const isoString = date.toISOString();
        return isoString.split('T')[0];
      } else {
        console.warn('Data non valida per timestampToDateInput:', timestamp);
        return '';
      }
    } catch (error) {
      console.error('Errore nella conversione timestamp:', error, 'Valore:', timestamp);
      return '';
    }
  };

  // Funzioni helper per ottenere nomi
  const getMezzoName = (mezzoId) => {
    if (!mezzoId) return 'Mezzo sconosciuto';
    const mezzo = mezzi.find(m => m.id === mezzoId);
    return mezzo ? (mezzo.nomeMezzo || mezzo.nome || 'Mezzo senza nome') : 'Mezzo non trovato';
  };

  const getMaterialeVariabileName = (materialeId) => {
    if (!materialeId) return 'Materiale sconosciuto';
    const materiale = materialiVariabili.find(m => m.id === materialeId);
    return materiale ? (materiale.nomeMacrovoce || materiale.nome || 'Materiale senza nome') : 'Materiale non trovato';
  };

  const getMaterialeFissoName = (materialeId) => {
    if (!materialeId) return 'Materiale sconosciuto';
    const materiale = materialiFissi.find(m => m.id === materialeId);
    return materiale ? (materiale.nomeMateriale || materiale.nome || 'Materiale senza nome') : 'Materiale non trovato';
  };

  const getLavorazioneName = (lavorazioneId) => {
    if (!lavorazioneId) return 'Lavorazione sconosciuta';
    const lavorazione = lavorazioni.find(l => l.id === lavorazioneId);
    return lavorazione ? (lavorazione.nomeLavorazione || lavorazione.nome || 'Lavorazione senza nome') : 'Lavorazione non trovata';
  };

  // Funzione per mostrare notifiche
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Funzione per filtrare i rapportini
  const filterRapportini = (rapportini, searchTerm) => {
    if (!searchTerm.trim()) {
      return rapportini;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    return rapportini.filter(rapportino => {
      const dataFormatted = formatRapportinoDate(rapportino.data).toLowerCase();
      if (dataFormatted.includes(searchLower)) return true;
      
      const redattore = getUserName(rapportino.redattoDa).toLowerCase();
      if (redattore.includes(searchLower)) return true;
      
      if (rapportino.noteVarie && rapportino.noteVarie.toLowerCase().includes(searchLower)) return true;
      
      if (rapportino.personalePresente && rapportino.personalePresente.some(persona => {
        const nomePersona = getPersonaName(persona).toLowerCase();
        return nomePersona.includes(searchLower);
      })) return true;
      
      if (rapportino.lavorazioni && rapportino.lavorazioni.some(lavorazione => {
        const nomeLavorazione = (lavorazione.nomeLavorazione || '').toLowerCase();
        return nomeLavorazione.includes(searchLower);
      })) return true;
      
      return false;
    });
  };

  // Funzione per aprire dropdown con posizione
  const handleOpenSettings = (rapportinoId, event) => {
    event.stopPropagation();
    
    const rapportinoTarget = filteredRapportini.find(r => r.id === rapportinoId);
    if (rapportinoTarget) {
      handleSelectRapportino(rapportinoTarget);
    }
    
    if (!isMobile) {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      });
    }
    
    setOpenSettingsDropdown(rapportinoId);
  };

  // Funzioni per le azioni del dropdown
  const handleModificaAvanzata = () => {
    if (selectedRapportino) {
      handleEdit(selectedRapportino);
    }
    setOpenSettingsDropdown(null);
  };

  const handleDuplicaRapportino = async () => {
    if (!selectedRapportino) {
      showNotification('⚠️ Nessun rapportino selezionato', 'warning');
      return;
    }
  
    // Apri modal per scegliere la data
    setRapportinoToDuplicate(selectedRapportino);
    setShowDateModal(true);
    setOpenSettingsDropdown(null);
  };
  
  // 4.4 AGGIUNGI handleDateConfirm (nuova funzione)
  const handleDateConfirm = async (selectedDate) => {
    setShowDateModal(false);
    
    if (!rapportinoToDuplicate) return;
  
    try {
      showNotification(
        `⏳ Duplicazione per ${new Date(selectedDate).toLocaleDateString('it-IT')}...`,
        'info'
      );
      
      const newRapportinoId = await duplicateRapportino(
        rapportinoToDuplicate.id, 
        cantiere.id,
        selectedDate // 🆕 PASSA LA DATA SCELTA
      );
      
      console.log(`✅ Rapportino duplicato: ${newRapportinoId} per ${selectedDate}`);
      
      await loadRapportini();
      
      setTimeout(async () => {
        const rapportiniAggiornati = await getRapportiniByCantiereId(cantiere.id);
        const nuovoRapportino = rapportiniAggiornati.find(r => r.id === newRapportinoId);
        if (nuovoRapportino) {
          handleSelectRapportino(nuovoRapportino);
        }
      }, 1000);
      
      showNotification(
        `✅ Rapportino duplicato per ${new Date(selectedDate).toLocaleDateString('it-IT')}!`,
        'success'
      );
      
    } catch (error) {
      console.error('❌ Errore:', error);
      showNotification(`❌ Errore: ${error.message}`, 'error');
    } finally {
      setRapportinoToDuplicate(null);
    }
  };
  
  // 4.5 AGGIUNGI handleDateCancel (nuova funzione)
  const handleDateCancel = () => {
    setShowDateModal(false);
    setRapportinoToDuplicate(null);
  };
  
  const handleEliminaRapportino = async () => {
    if (!selectedRapportino || !window.confirm('Sei sicuro di voler eliminare questo rapportino?')) return;
    
    try {
      await deleteRapportino(selectedRapportino.id);
      await loadRapportini();
      setSelectedRapportino(null);
      setFormData({});
      showNotification('Rapportino eliminato con successo', 'success');
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      showNotification('Errore nell\'eliminazione del rapportino', 'error');
    }
    setOpenSettingsDropdown(null);
  };

  // Reset form
  const resetForm = () => {
    const cleanFormData = {
      data: new Date().toISOString().split('T')[0],
      redattoDa: currentUser?.uid || '',
      personalePresente: [],
      mezziUtilizzati: [],
      materialiVariabili: [],
      materialiFissi: [],
      lavorazioni: [],
      // 🔴 RIMUOVI COMPLETAMENTE: ricavi: [], // Nascosto per responsabili
      noteVarie: '',
      allegati: []
    };
    
    setFormData(cleanFormData);
    setEditingRapportino(null);
    setShowForm(false);
  };

  // Handlers per le azioni sui rapportini
  const handleSelectRapportino = (rapportino) => {
    console.log('Selecting rapportino:', rapportino);
    
    setSelectedRapportino(rapportino);
    setEditingRapportino(rapportino);
    
    const safeFormData = {
      data: timestampToDateInput(rapportino.data) || new Date().toISOString().split('T')[0],
      redattoDa: rapportino.redattoDa || '',
      personalePresente: Array.isArray(rapportino.personalePresente) ? rapportino.personalePresente : [],
      mezziUtilizzati: Array.isArray(rapportino.mezziUtilizzati) ? rapportino.mezziUtilizzati : [],
      materialiVariabili: Array.isArray(rapportino.materialiVariabili) ? rapportino.materialiVariabili : [],
      materialiFissi: Array.isArray(rapportino.materialiFissi) ? rapportino.materialiFissi : [],
      lavorazioni: Array.isArray(rapportino.lavorazioni) ? rapportino.lavorazioni : [],
      // 🔴 RIMUOVI COMPLETAMENTE: ricavi: Array.isArray(rapportino.ricavi) ? rapportino.ricavi : [],
      noteVarie: typeof rapportino.noteVarie === 'string' ? rapportino.noteVarie : '',
      allegati: Array.isArray(rapportino.allegati) ? rapportino.allegati : [],
      modificatoDa: rapportino.modificatoDa || '',
      dataModifica: rapportino.dataModifica ? timestampToDateInput(rapportino.dataModifica) : ''
    };
    
    setFormData(safeFormData);
    setOpenSettingsDropdown(null);
  };

  const handleEdit = (rapportino) => {
    console.log('Editing rapportino:', rapportino);
    
    setEditingRapportino(rapportino);
    
    const safeFormData = {
      data: timestampToDateInput(rapportino.data) || new Date().toISOString().split('T')[0],
      redattoDa: rapportino.redattoDa || currentUser?.uid || '',
      personalePresente: Array.isArray(rapportino.personalePresente) ? rapportino.personalePresente : [],
      mezziUtilizzati: Array.isArray(rapportino.mezziUtilizzati) ? rapportino.mezziUtilizzati : [],
      materialiVariabili: Array.isArray(rapportino.materialiVariabili) ? rapportino.materialiVariabili : [],
      materialiFissi: Array.isArray(rapportino.materialiFissi) ? rapportino.materialiFissi : [],
      lavorazioni: Array.isArray(rapportino.lavorazioni) ? rapportino.lavorazioni : [],
      // 🔴 RIMUOVI COMPLETAMENTE: ricavi: Array.isArray(rapportino.ricavi) ? rapportino.ricavi : [],
      noteVarie: typeof rapportino.noteVarie === 'string' ? rapportino.noteVarie : '',
      allegati: Array.isArray(rapportino.allegati) ? rapportino.allegati : [],
      createdAt: rapportino.createdAt ? timestampToDateInput(rapportino.createdAt) : '',
      updatedAt: rapportino.updatedAt ? timestampToDateInput(rapportino.updatedAt) : '',
      modificatoDa: rapportino.modificatoDa || '',
      dataModifica: rapportino.dataModifica ? timestampToDateInput(rapportino.dataModifica) : ''
    };
    
    console.log('Safe form data:', safeFormData);
    
    setFormData(safeFormData);
    setShowForm(true);
    setOpenSettingsDropdown(null);
  };

  // Form submission handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.data) {
      showNotification('La data è obbligatoria', 'error');
      return;
    }

    if (new Date(formData.data) > new Date()) {
      showNotification('Non è possibile inserire una data futura', 'error');
      return;
    }

    const hasPersonale = formData.personalePresente?.length > 0;
    const hasMezzi = formData.mezziUtilizzati?.length > 0;
    const hasMateriali = (formData.materialiVariabili?.length > 0) || (formData.materialiFissi?.length > 0);
    const hasLavorazioni = formData.lavorazioni?.length > 0;
    // 🔴 RIMUOVI COMPLETAMENTE: const hasRicavi = formData.ricavi?.length > 0;

    if (!hasPersonale && !hasMezzi && !hasMateriali && !hasLavorazioni) {
      showNotification('Inserire almeno una sezione del rapportino', 'error');
      return;
    }

    try {
      const rapportinoData = {
        ...formData,
        cantiereId: cantiere.id,
        nomeRapportino: formData.data,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser.uid
      };

      if (editingRapportino) {
        rapportinoData.modificatoDa = currentUser.uid;
        rapportinoData.modificatoIl = new Date();
        rapportinoData.modifiche = [
          ...(editingRapportino.modifiche || []),
          {
            data: new Date(),
            utente: currentUser.uid,
            nomeUtente: `${currentUser.nome} ${currentUser.cognome}` || currentUser.email
          }
        ];

        await updateRapportino(editingRapportino.id, rapportinoData);
        showNotification('Rapportino aggiornato con successo', 'success');
      } else {
        await createRapportino(rapportinoData);
        showNotification('Rapportino creato con successo', 'success');
      }

      // 🆕 AGGIUNGERE QUESTO BLOCCO:
try {
  const syncResult = await syncPendingManualHours(
    formData.personalePresente, 
    formData.data
  );
  
  if (syncResult.syncedCount > 0) {
    console.log(`✅ ${syncResult.syncedCount} ore manuali sincronizzate`);
  }
  
  if (syncResult.errorCount > 0) {
    showNotification(
      editingRapportino 
        ? 'Rapportino aggiornato, ma alcune ore non sincronizzate' 
        : 'Rapportino creato, ma alcune ore non sincronizzate',
      'warning'
    );
  } else {
    showNotification(
      editingRapportino 
        ? 'Rapportino aggiornato con successo' 
        : 'Rapportino creato con successo',
      'success'
    );
  }
} catch (syncError) {
  console.error('Errore sincronizzazione:', syncError);
  showNotification(
    editingRapportino 
      ? 'Rapportino aggiornato, errore sincronizzazione ore' 
      : 'Rapportino creato, errore sincronizzazione ore',
    'warning'
  );
}

      resetForm();
      await loadRapportini();
      
      // 🆕 SISTEMA AUTOMATICO: Distribuzione automatica dopo salvataggio
if (formData.personalePresente && formData.personalePresente.length > 0) {
  const userIds = formData.personalePresente
    .filter(p => p.statoPresenza === 'presente' && p.personaId)
    .map(p => p.personaId);
  
  if (userIds.length > 0) {
    console.log(`🔄 [RESPONSABILE] Avvio distribuzione automatica per ${userIds.length} persone`);
    
    setTimeout(async () => {
      try {
        const results = await triggerBatchDistribution(userIds, formData.data);
        
        if (results.successful > 0) {
          console.log(`✅ [RESPONSABILE] Distribuzione automatica completata: ${results.successful}/${results.total} persone`);
          await loadRapportini();
          
          showNotification(
            `Rapportino salvato e ore distribuite automaticamente per ${results.successful} persone`, 
            'success'
          );
        } else if (results.errors > 0) {
          showNotification(
            `Rapportino salvato ma alcune distribuzioni hanno avuto errori`, 
            'warning'
          );
        }
      } catch (error) {
        console.error('❌ [RESPONSABILE] Errore distribuzione automatica post-salvataggio:', error);
        showNotification(
          'Rapportino salvato ma errore nella distribuzione automatica', 
          'warning'
        );
      }
    }, 2000);
  }
}

    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      showNotification(error.message || 'Errore nel salvataggio del rapportino', 'error');
    }
  };

  // Aggiornamento rapido dei dati
  const handleQuickUpdate = async (section, data) => {
    if (!selectedRapportino) return;
    
    setIsUpdating(true);
    try {
      const updatedFormData = {
        ...formData,
        [section]: data
      };
      setFormData(updatedFormData);
      
      const updatedData = {
        ...selectedRapportino,
        [section]: data,
        modificatoDa: currentUser.uid,
        dataModifica: new Date().toISOString()
      };
      
      await updateRapportino(selectedRapportino.id, updatedData);

      // 🆕 Sincronizza col monte ore (workHours) anche per le modifiche rapide:
      // le righe di personale marcate con pendingManualHours (es. da "Applica a tutti i presenti")
      // vengono scritte nel monte ore esattamente come nel form completo.
      if (section === 'personalePresente' && Array.isArray(data)) {
        try {
          const syncResult = await syncPendingManualHours(data, updatedFormData.data);
          if (syncResult.syncedCount > 0) {
            console.log(`✅ [RESPONSABILE-QUICK] ${syncResult.syncedCount} ore sincronizzate nel monte ore`);
          }
          if (syncResult.errorCount > 0) {
            showNotification('Personale aggiornato, ma alcune ore non sincronizzate nel monte ore', 'warning');
          }
        } catch (syncError) {
          console.error('❌ [RESPONSABILE-QUICK] Errore sincronizzazione monte ore:', syncError);
        }
      }

      // 🆕 SISTEMA AUTOMATICO: Distribuzione per aggiornamenti rapidi
if (section === 'personalePresente' && Array.isArray(data)) {
  const userIds = data
    .filter(p => p.statoPresenza === 'presente' && p.personaId)
    .map(p => p.personaId);
  
  if (userIds.length > 0) {
    console.log(`🔄 [RESPONSABILE] Distribuzione automatica per aggiornamento rapido: ${userIds.length} persone`);
    
    setTimeout(async () => {
      try {
        const results = await triggerBatchDistribution(userIds, formData.data);
        
        if (results.successful > 0) {
          console.log(`✅ [RESPONSABILE] Distribuzione rapida completata: ${results.successful} persone`);
          await loadRapportini();
        }
      } catch (error) {
        console.error('❌ [RESPONSABILE] Errore distribuzione rapida:', error);
      }
    }, 1500);
  }
}
      
      await loadRapportini();
      
      const updatedRapportini = await getRapportiniByCantiereId(cantiere.id);
      const updatedSelected = updatedRapportini.find(r => r.id === selectedRapportino.id);
      if (updatedSelected) {
        setSelectedRapportino(updatedSelected);
      }
      
      showNotification('Rapportino aggiornato con successo', 'success');
    } catch (error) {
      console.error('Errore nell\'aggiornamento:', error);
      showNotification('Errore nell\'aggiornamento del rapportino', 'error');
      setFormData(selectedRapportino);
    } finally {
      setIsUpdating(false);
    }
  };

  // Funzioni per gestire le selezioni dai suggerimenti (SENZA COSTI)
  const handleSelectPersona = (user) => {
    const personalePresente = formData.personalePresente || [];
    const giaPresente = personalePresente.some(p => 
      p.personaId === user.id || p.userId === user.id
    );
    
    if (giaPresente) {
      showNotification('Questa persona è già presente nel rapportino', 'error');
      return;
    }
    
    const newPersona = {
      personaId: user.id,
      userId: user.id,
      nome: user.nome,
      cognome: user.cognome,
      incarico: user.incarico || 'Operaio',
      oreLavorate: 0,
      oreStandard: 0,
      oreStraordinario: 0,
      // 🔧 FIX: SALVA SEMPRE il costo (anche se nascosto nell'interfaccia)
      costoOrario: parseFloat(user.costoOrario || 0),
      totale: 0, // Sarà calcolato quando si inseriscono le ore
      statoPresenza: 'presente'
    };
    const newPersonale = [...personalePresente, newPersona];
    
    setFormData(prev => ({
      ...prev,
      personalePresente: newPersonale
    }));
    
    handleQuickUpdate('personalePresente', newPersonale);
    setSearchPersonale('');
  };

  /**
 * 🔧 FIX: Ricalcola automaticamente il totale quando cambiano le ore
 */
const handleUpdatePersonaleOre = (index, field, value) => {
  const personalePresente = [...(formData.personalePresente || [])];
  const persona = { ...personalePresente[index] };
  
  const numValue = parseFloat(value) || 0;
  persona[field] = numValue;
  
  // Se sono state modificate ore, ricalcola
  if (field === 'oreStandard' || field === 'oreStraordinario' || field === 'oreLavorate') {
    const oreStandard = parseFloat(persona.oreStandard) || 0;
    const oreStraordinario = parseFloat(persona.oreStraordinario) || 0;
    persona.oreLavorate = oreStandard + oreStraordinario;
    
    // 🔧 RICALCOLA SEMPRE IL TOTALE
    const costoOrario = parseFloat(persona.costoOrario || 0);
    persona.totale = persona.oreLavorate * costoOrario;
    
    console.log(`💰 [RESPONSABILE] Ricalcolo: ${persona.nome} - ${persona.oreLavorate}h × €${costoOrario} = €${persona.totale.toFixed(2)}`);
  }
  
  personalePresente[index] = persona;
  
  setFormData(prev => ({
    ...prev,
    personalePresente: personalePresente
  }));
  
  handleQuickUpdate('personalePresente', personalePresente);
};

  const handleSelectMezzo = (mezzo) => {
    const mezziUtilizzati = formData.mezziUtilizzati || [];
    const giaPresente = mezziUtilizzati.some(m => m.mezzoId === mezzo.id);
    
    if (giaPresente) {
      showNotification('Questo mezzo è già presente nel rapportino', 'error');
      return;
    }
    
    const newMezzo = {
      mezzoId: mezzo.id,
      nomeMezzo: mezzo.nomeMezzo || mezzo.nome,
      targa: mezzo.targa || '',
      ore: 0,
      // 🔧 FIX: Mantieni sempre il costo orario dal database
      costoOrario: parseFloat(mezzo.costoOrario || 0),
      totale: 0 // Verrà calcolato quando si inseriscono le ore
    };
    const newMezzi = [...mezziUtilizzati, newMezzo];
    
    setFormData(prev => ({
      ...prev,
      mezziUtilizzati: newMezzi
    }));
    
    handleQuickUpdate('mezziUtilizzati', newMezzi);
    setSearchMezzi('');
  };
  

  const handleSelectMaterialeVariabile = (materiale) => {
    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    const giaPresente = materialiVariabiliPresenti.some(m => m.materialeId === materiale.id);
    
    if (giaPresente) {
      showNotification('Questo materiale è già presente nel rapportino', 'error');
      return;
    }
    
    const newMateriale = {
      materialeId: materiale.id,
      nomeMacrovoce: materiale.nomeMacrovoce || materiale.nome,
      descrizione: '',
      // 🔴 RIMUOVI COMPLETAMENTE: costo: nascosto per responsabili
    };
    const newMateriali = [...materialiVariabiliPresenti, newMateriale];
    
    setFormData(prev => ({
      ...prev,
      materialiVariabili: newMateriali
    }));
    
    handleQuickUpdate('materialiVariabili', newMateriali);
    setSearchMatVariabili('');
  };

  const handleSelectMaterialeFisso = (materiale) => {
    const materialiFissiPresenti = formData.materialiFissi || [];
    const giaPresente = materialiFissiPresenti.some(m => m.materialeId === materiale.id);
    
    if (giaPresente) {
      showNotification('Questo materiale è già presente nel rapportino', 'error');
      return;
    }
    
    // 🔧 FIX: Calcola sempre i prezzi per mantenere la consistenza
    const prezzoBase = parseFloat(materiale.prezzoUnitario || 0);
    const costoTrasporto = parseFloat(cantiere.variazioneCostiMateriali || 0);
    const prezzoTotale = prezzoBase + costoTrasporto;
    
    const newMateriale = {
      materialeId: materiale.id,
      nomeMateriale: materiale.nomeMateriale || materiale.nome,
      unitaMisura: materiale.unitaMisura || 'pz',
      quantita: 0,
      // 🔧 FIX: Salva sempre i dati di costo
      prezzoUnitarioBase: prezzoBase,
      costoTrasporto: costoTrasporto,
      prezzoUnitario: prezzoTotale,
      totale: 0 // Verrà calcolato quando si inserisce la quantità
    };
    const newMateriali = [...materialiFissiPresenti, newMateriale];
    
    setFormData(prev => ({ ...prev, materialiFissi: newMateriali }));
    handleQuickUpdate('materialiFissi', newMateriali);
    setSearchMatFissi('');
  };

  const handleSelectLavorazione = (lavorazione) => {
    const lavorazioniPresenti = formData.lavorazioni || [];
    const giaPresente = lavorazioniPresenti.some(l => l.lavorazioneId === lavorazione.id);
    
    if (giaPresente) {
      showNotification('Questa lavorazione è già presente nel rapportino', 'error');
      return;
    }
    
    const newLavorazione = {
      lavorazioneId: lavorazione.id,
      nomeLavorazione: lavorazione.nomeLavorazione || lavorazione.nome,
      descrizione: '',
      note: '',
      isCustom: false
    };
    const newLavorazioni = [...lavorazioniPresenti, newLavorazione];
    
    setFormData(prev => ({ ...prev, lavorazioni: newLavorazioni }));
    handleQuickUpdate('lavorazioni', newLavorazioni);
    setSearchLavorazioni('');
  };

  // Funzioni per filtrare i suggerimenti disponibili
  const getAvailableUsers = () => {
    return users.filter(u => !formData.personalePresente?.some(p => 
      p.personaId === u.id || p.userId === u.id
    ));
  };

  const getAvailableMezzi = () => {
    return mezzi.filter(m => !formData.mezziUtilizzati?.some(mu => mu.mezzoId === m.id));
  };

  const getAvailableMaterialiVariabili = () => {
    return materialiVariabili.filter(m => !formData.materialiVariabili?.some(mv => mv.materialeId === m.id));
  };

  const getAvailableMaterialiFissi = () => {
    return materialiFissi.filter(m => !formData.materialiFissi?.some(mf => mf.materialeId === m.id));
  };

  const getAvailableLavorazioni = () => {
    return lavorazioni.filter(l => !formData.lavorazioni?.some(fl => fl.lavorazioneId === l.id));
  };

 // ========================================
// COMPONENTI INPUT CON STATO LOCALE
// Per evitare perdita di focus durante la digitazione
// ========================================

// Input per numeri (ore, quantità, etc.)
const DebouncedNumberInput = ({ 
  value, 
  onUpdate, 
  step = "0.5", 
  min = "0",
  placeholder = "",
  ariaLabel = "",
  delay = 2000,
  className = "form-control table-input"
}) => {
  const [localValue, setLocalValue] = useState(value || 0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || 0);
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onUpdate(newValue);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <input
      type="number"
      value={localValue}
      onChange={handleChange}
      step={step}
      min={min}
      placeholder={placeholder}
      className={className}
      aria-label={ariaLabel}
    />
  );
};

// Input per testo (note, descrizioni, etc.)
const DebouncedTextInput = ({ 
  value, 
  onUpdate, 
  placeholder = "",
  ariaLabel = "",
  delay = 2000,
  className = "form-control table-input"
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onUpdate(newValue);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <input
      type="text"
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      aria-label={ariaLabel}
    />
  );
};

  // ========================================
  // SEZIONI FORM COMPLETO (SENZA COSTI E RICAVI)
  // ========================================

  // Wrapper per la sezione personale automatica
  const PersonaleSectionWrapper = () => {
    if (!formData || typeof formData !== 'object') {
      console.error('PersonaleSection: formData non valido:', formData);
      return (
        <div className="form-section">
          <h4>👷 Personale Presente</h4>
          <div className="alert alert-danger">
            Errore nel caricamento dati. Ricarica la pagina.
          </div>
        </div>
      );
    }

    if (!users || users.length === 0) {
      console.warn('PersonaleSection: Nessun utente caricato');
      return (
        <div className="form-section">
          <h4>👷 Personale Presente</h4>
          <div className="alert alert-warning">
            Caricamento utenti in corso...
          </div>
        </div>
      );
    }

    if (!formData.data) {
      return (
        <div className="form-section">
          <h4>👷 Personale Presente</h4>
          <div className="alert alert-info">
            ⚠️ Seleziona prima la data del rapportino per vedere le ore disponibili
          </div>
        </div>
      );
    }

    return (
      <AutoPersonaleSectionResponsabili 
        users={users}
        formData={formData}
        setFormData={setFormData}
        rapportinoDate={formData.data}
        editingRapportino={editingRapportino}
      />
    );
  };

  // Sezione Mezzi Completa (SENZA COSTI)
  const MezziSection = () => {
    const [selectedMezzo, setSelectedMezzo] = useState('');
    const [oreMezzo, setOreMezzo] = useState('');
  
    if (!formData || typeof formData !== 'object') {
      console.error('MezziSection: formData non valido');
      return (
        <div className="form-section">
          <h4>🚛 Mezzi Utilizzati</h4>
          <div className="alert alert-danger">
            Errore nel caricamento dati. Ricarica la pagina.
          </div>
            {/* 🆕 PULSANTE COPIA MEZZI */}
    <div className="section-header-with-actions" style={{ marginBottom: '15px' }}>
      <button
        type="button"
        onClick={handleOpenCopyMezziModal}
        className="btn btn-secondary btn-copy-mezzi"
        title="Copia mezzi e ore da un rapportino precedente"
      >
        📋 Copia da giorno precedente
      </button>
    </div>
        </div>
      );
    }
  
    const addMezzo = () => {
      if (!selectedMezzo || !oreMezzo) {
        showNotification('Seleziona un mezzo e inserisci le ore di utilizzo', 'error');
        return;
      }
      
      const mezzo = mezzi.find(m => m.id === selectedMezzo);
      if (!mezzo) {
        showNotification('Mezzo non trovato', 'error');
        return;
      }
    
      const mezziUtilizzati = formData.mezziUtilizzati || [];
      const esisteGia = mezziUtilizzati.some(m => m.mezzoId === selectedMezzo);
      if (esisteGia) {
        showNotification('Questo mezzo è già stato aggiunto', 'error');
        return;
      }
    
      const oreNum = parseFloat(oreMezzo);
      if (oreNum <= 0) {
        showNotification('Le ore devono essere maggiori di zero', 'error');
        return;
      }
    
      // 🔧 FIX: Calcola sempre il costo anche se nascosto
      const costoOrario = parseFloat(mezzo.costoOrario || 0);
    
      const newEntry = {
        mezzoId: selectedMezzo,
        nomeMezzo: mezzo.nomeMezzo || mezzo.nome,
        targa: mezzo.targa || '',
        ore: oreNum,
        oreLavorate: oreNum,
        // 🔧 FIX: Salva sempre i dati di costo
        costoOrario: costoOrario,
        totale: oreNum * costoOrario
      };
    
      setFormData(prev => ({
        ...prev,
        mezziUtilizzati: [...mezziUtilizzati, newEntry]
      }));
    
      setSelectedMezzo('');
      setOreMezzo('');
    };
  
    const removeMezzo = (index) => {
      const mezziUtilizzati = formData.mezziUtilizzati || [];
      setFormData(prev => ({
        ...prev,
        mezziUtilizzati: mezziUtilizzati.filter((_, i) => i !== index)
      }));
    };
  
    const updateOre = (index, newOre) => {
      const mezziUtilizzati = formData.mezziUtilizzati || [];
      const updatedMezzi = [...mezziUtilizzati];
      const oreNum = parseFloat(newOre) || 0;
      
      // 🔧 FIX: Assicurati che il costo orario sia presente
      const mezzoData = mezzi.find(m => m.id === updatedMezzi[index].mezzoId);
      const costoOrario = parseFloat(mezzoData?.costoOrario || updatedMezzi[index].costoOrario || 0);
      
      updatedMezzi[index] = {
        ...updatedMezzi[index],
        ore: oreNum,
        oreLavorate: oreNum,
        // 🔧 FIX: Calcola sempre il totale, anche se nascosto ai responsabili
        costoOrario: costoOrario,
        totale: oreNum * costoOrario
      };
      
      setFormData(prev => ({
        ...prev,
        mezziUtilizzati: updatedMezzi
      }));
    };
  
    const mezziUtilizzatiPresenti = formData.mezziUtilizzati || [];
  
    return (
      <div className="form-section">
        {/* 🆕 SEZIONE HEADER CON PULSANTE COPIA */}
        <div className="section-header-with-actions" style={{ marginBottom: '15px' }}>
          <button
            type="button"
            onClick={handleOpenCopyMezziModal}
            className="btn btn-secondary btn-copy-mezzi"
            title="Copia mezzi e ore da un rapportino precedente"
          >
            📋 Copia da giorno precedente
          </button>
        </div>
        
        <div className="mezzi-form">
          <div className="form-row">
            <div className="form-group">
              <MezziAutocomplete
                mezzi={mezzi}
                selectedMezzo={selectedMezzo}
                setSelectedMezzo={setSelectedMezzo}
                onSelect={(mezzo) => setSelectedMezzo(mezzo.id)}
              />
            </div>
            
            <div className="form-group">
              <label>Ore di utilizzo *</label>
              <input
                type="number"
                value={oreMezzo}
                onChange={(e) => setOreMezzo(e.target.value)}
                step="0.5"
                min="0.5"
                placeholder="8.0"
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <button 
                type="button" 
                onClick={addMezzo}
                className="btn btn-primary"
                disabled={!selectedMezzo || !oreMezzo}
              >
                ➕ Aggiungi
              </button>
            </div>
          </div>
        </div>
        
        {/* Lista mezzi aggiunti (SENZA COSTI) */}
        {mezziUtilizzatiPresenti.length > 0 && (
          <div className="mezzi-list">
            <table className="table">
              <thead>
                <tr>
                  <th>Mezzo</th>
                  <th>Targa</th>
                  <th>Ore</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {mezziUtilizzatiPresenti.map((mezzo, index) => (
                  <tr key={mezzo.mezzoId || `mezzo-${index}`}> {/* ✅ KEY CORRETTA */}
                    <td data-label="Mezzo">{mezzo.nomeMezzo || getMezzoName(mezzo.mezzoId)}</td>
                    <td data-label="Targa">{mezzo.targa || '-'}</td>
                    <td data-label="Ore">
                      {/* ✅ INPUT DEBOUNCED - MANTIENE IL FOCUS */}
                      <DebouncedNumberInput
                        value={mezzo.ore || mezzo.oreLavorate || 0}
                        onUpdate={(newValue) => updateOre(index, newValue)}
                        step="0.5"
                        min="0"
                        ariaLabel={`Ore mezzo ${mezzo.nomeMezzo}`}
                      />
                    </td>
                    <td data-label="Azioni">
                      <button 
                        type="button"
                        onClick={() => removeMezzo(index)}
                        className="btn btn-danger btn-sm"
                        title="Rimuovi mezzo"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Sezione Materiali Variabili Completa (SENZA COSTI)
const MaterialiVariabiliSection = () => {
  const [selectedMateriale, setSelectedMateriale] = useState('');
  const [descrizione, setDescrizione] = useState('');

  if (!formData || typeof formData !== 'object') {
    console.error('MaterialiVariabiliSection: formData non valido');
    return (
      <div className="form-section">
        <h4>📦 Materiali a Costo Variabile</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addMaterialeVariabile = () => {
    if (!selectedMateriale) {
      showNotification('Seleziona un materiale', 'error');
      return;
    }
    
    const materiale = materialiVariabili.find(m => m.id === selectedMateriale);
    if (!materiale) {
      showNotification('Materiale non trovato', 'error');
      return;
    }

    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    const esisteGia = materialiVariabiliPresenti.some(m => m.materialeId === selectedMateriale);
    if (esisteGia) {
      showNotification('Questo materiale è già stato aggiunto', 'error');
      return;
    }

    const newEntry = {
      materialeId: selectedMateriale,
      nomeMacrovoce: materiale.nomeMacrovoce || materiale.nome,
      descrizione: descrizione.trim() || '',
      // 🔴 NESSUN COSTO per responsabili
    };

    setFormData(prev => ({
      ...prev,
      materialiVariabili: [...materialiVariabiliPresenti, newEntry]
    }));

    setSelectedMateriale('');
    setDescrizione('');
  };

  const removeMaterialeVariabile = (index) => {
    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    setFormData(prev => ({
      ...prev,
      materialiVariabili: materialiVariabiliPresenti.filter((_, i) => i !== index)
    }));
  };

  const updateDescrizione = (index, newDescrizione) => {
    const materialiVariabiliPresenti = formData.materialiVariabili || [];
    const updatedMateriali = [...materialiVariabiliPresenti];
    
    updatedMateriali[index] = {
      ...updatedMateriali[index],
      descrizione: newDescrizione
    };

    setFormData(prev => ({
      ...prev,
      materialiVariabili: updatedMateriali
    }));
  };

  const materialiVariabiliPresenti = formData.materialiVariabili || [];

  return (
    <div className="form-section">
      
      <div className="info-materiali-variabili">
        <small className="text-info">
          💡 <strong>La descrizione è facoltativa:</strong> Puoi aggiungere dettagli specifici solo se necessario
        </small>
      </div>

      <div className="materiali-form">
        <div className="form-row">
          <div className="form-group">
            <MaterialiVariabiliAutocomplete
              materialiVariabili={materialiVariabili}
              selectedMateriale={selectedMateriale}
              setSelectedMateriale={setSelectedMateriale}
              onSelect={(materiale) => setSelectedMateriale(materiale.id)}
            />
          </div>
          
          <div className="form-group">
            <label>Descrizione (facoltativa)</label>
            <input
              type="text"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Dettagli aggiuntivi se necessari..."
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <button 
              type="button" 
              onClick={addMaterialeVariabile}
              className="btn btn-primary"
              disabled={!selectedMateriale}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>
      </div>
      
      {/* Lista materiali aggiunti (SENZA COSTI) */}
      {materialiVariabiliPresenti.length > 0 && (
        <div className="materiali-list">
          <table className="table">
            <thead>
              <tr>
                <th>Materiale</th>
                <th>Descrizione</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {materialiVariabiliPresenti.map((materiale, index) => (
                <tr key={materiale.materialeId || `materiale-var-${index}`}> {/* ✅ KEY CORRETTA */}
                  <td data-label="Materiale">
                    {materiale.nomeMacrovoce || getMaterialeVariabileName(materiale.materialeId)}
                  </td>
                  <td data-label="Descrizione">
                    {/* ✅ INPUT DEBOUNCED - MANTIENE IL FOCUS */}
                    <DebouncedTextInput
                      value={materiale.descrizione || ''}
                      onUpdate={(newValue) => updateDescrizione(index, newValue)}
                      placeholder="Descrizione facoltativa..."
                      ariaLabel={`Descrizione ${materiale.nomeMacrovoce}`}
                    />
                  </td>
                  <td data-label="Azioni">
                    <button 
                      type="button"
                      onClick={() => removeMaterialeVariabile(index)}
                      className="btn btn-danger btn-sm"
                      title="Rimuovi materiale"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Sezione Materiali Fissi Completa (SENZA COSTI)
const MaterialiFissiSection = () => {
  const [selectedMateriale, setSelectedMateriale] = useState('');
  const [quantita, setQuantita] = useState('');

  if (!formData || typeof formData !== 'object') {
    console.error('MaterialiFissiSection: formData non valido');
    return (
      <div className="form-section">
        <h4>🏗️ Materiali a Costo Fisso</h4>
        <div className="alert alert-danger">
          Errore nel caricamento dati. Ricarica la pagina.
        </div>
      </div>
    );
  }

  const addMaterialeFisso = () => {
    if (!selectedMateriale || !quantita) {
      showNotification('Seleziona un materiale e inserisci la quantità', 'error');
      return;
    }
    
    const materiale = materialiFissi.find(m => m.id === selectedMateriale);
    if (!materiale) {
      showNotification('Materiale non trovato', 'error');
      return;
    }
  
    const materialiFissiPresenti = formData.materialiFissi || [];
    const esisteGia = materialiFissiPresenti.some(m => m.materialeId === selectedMateriale);
    if (esisteGia) {
      showNotification('Questo materiale è già stato aggiunto', 'error');
      return;
    }
  
    const quantitaNum = parseFloat(quantita);
    if (quantitaNum <= 0) {
      showNotification('La quantità deve essere maggiore di zero', 'error');
      return;
    }
  
    // 🔧 FIX: Calcola sempre i prezzi anche se nascosti
    const prezzoBase = parseFloat(materiale.prezzoUnitario || 0);
    const costoTrasporto = parseFloat(cantiere.variazioneCostiMateriali || 0);
    const prezzoTotale = prezzoBase + costoTrasporto;
    const totale = quantitaNum * prezzoTotale;
  
    const newEntry = {
      materialeId: selectedMateriale,
      nomeMateriale: materiale.nomeMateriale || materiale.nome,
      unitaMisura: materiale.unitaMisura || 'pz',
      quantita: quantitaNum,
      // 🔧 FIX: Salva sempre tutti i dati di costo
      prezzoUnitarioBase: prezzoBase,
      costoTrasporto: costoTrasporto,
      prezzoUnitario: prezzoTotale,
      totale: totale
    };
  
    setFormData(prev => ({
      ...prev,
      materialiFissi: [...materialiFissiPresenti, newEntry]
    }));
  
    setSelectedMateriale('');
    setQuantita('');
  };

  const removeMaterialeFisso = (index) => {
    const materialiFissiPresenti = formData.materialiFissi || [];
    setFormData(prev => ({
      ...prev,
      materialiFissi: materialiFissiPresenti.filter((_, i) => i !== index)
    }));
  };

  const updateQuantita = (index, newQuantita) => {
    const materialiFissiPresenti = formData.materialiFissi || [];
    const updatedMateriali = [...materialiFissiPresenti];
    const quantitaNum = parseFloat(newQuantita) || 0;
    
    // 🔧 FIX: Assicurati che il prezzo unitario sia presente
    const materialeData = materialiFissi.find(m => m.id === updatedMateriali[index].materialeId);
    let prezzoUnitario = parseFloat(updatedMateriali[index].prezzoUnitario || 0);
    
    // Se non è presente nel formData, ricalcolalo dal database
    if (prezzoUnitario === 0 && materialeData) {
      const prezzoBase = parseFloat(materialeData.prezzoUnitario || 0);
      const costoTrasporto = parseFloat(cantiere.variazioneCostiMateriali || 0);
      prezzoUnitario = prezzoBase + costoTrasporto;
    }
    
    updatedMateriali[index] = {
      ...updatedMateriali[index],
      quantita: quantitaNum,
      // 🔧 FIX: Mantieni tutti i dati di costo anche se nascosti
      prezzoUnitarioBase: materialeData ? parseFloat(materialeData.prezzoUnitario || 0) : (updatedMateriali[index].prezzoUnitarioBase || 0),
      costoTrasporto: parseFloat(cantiere.variazioneCostiMateriali || 0),
      prezzoUnitario: prezzoUnitario,
      totale: quantitaNum * prezzoUnitario
    };
    
    setFormData(prev => ({
      ...prev,
      materialiFissi: updatedMateriali
    }));
  };

  const materialiFissiPresenti = formData.materialiFissi || [];

  return (
    <div className="form-section">

      <div className="materiali-fissi-form">
        <div className="form-row">
          <div className="form-group">
            <MaterialiFissiAutocomplete
              materialiFissi={materialiFissi}
              selectedMateriale={selectedMateriale}
              setSelectedMateriale={setSelectedMateriale}
              onSelect={(materiale) => setSelectedMateriale(materiale.id)}
            />
          </div>
          
          <div className="form-group">
            <label>Quantità *</label>
            <input
              type="number"
              value={quantita}
              onChange={(e) => setQuantita(e.target.value)}
              step="0.01"
              min="0.01"
              placeholder="10.5"
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <button 
              type="button" 
              onClick={addMaterialeFisso}
              className="btn btn-primary"
              disabled={!selectedMateriale || !quantita}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>
      </div>
      
      {/* Lista materiali aggiunti (SENZA COSTI) */}
      {materialiFissiPresenti.length > 0 && (
        <div className="materiali-fissi-list">
          <table className="table">
            <thead>
              <tr>
                <th>Materiale</th>
                <th>U.M.</th>
                <th>Quantità</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {materialiFissiPresenti.map((materiale, index) => (
                <tr key={materiale.materialeId || `materiale-fisso-${index}`}> {/* ✅ KEY CORRETTA */}
                  <td data-label="Materiale">
                    {materiale.nomeMateriale || getMaterialeFissoName(materiale.materialeId)}
                  </td>
                  <td data-label="U.M.">
                    {materiale.unitaMisura || 'pz'}
                  </td>
                  <td data-label="Quantità">
                    {/* ✅ INPUT DEBOUNCED - MANTIENE IL FOCUS */}
                    <DebouncedNumberInput
                      value={materiale.quantita || 0}
                      onUpdate={(newValue) => updateQuantita(index, newValue)}
                      step="0.01"
                      min="0"
                      ariaLabel={`Quantità ${materiale.nomeMateriale}`}
                    />
                  </td>
                  <td data-label="Azioni">
                    <button 
                      type="button"
                      onClick={() => removeMaterialeFisso(index)}
                      className="btn btn-danger btn-sm"
                      title="Rimuovi materiale"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

  // Sezione Lavorazioni Completa
  const LavorazioniSection = () => {
    const [selectedLavorazione, setSelectedLavorazione] = useState('');
    const [note, setNote] = useState('');
    const [isNewLavorazione, setIsNewLavorazione] = useState(false);
    const [newLavorazioneName, setNewLavorazioneName] = useState('');

    if (!formData || typeof formData !== 'object') {
      console.error('LavorazioniSection: formData non valido');
      return (
        <div className="form-section">
          <h4>⚒️ Lavorazioni Eseguite</h4>
          <div className="alert alert-danger">
            Errore nel caricamento dati. Ricarica la pagina.
          </div>
        </div>
      );
    }

    const addLavorazione = () => {
      let lavorazioneName = '';
      let lavorazioneId = '';
      let isCustom = false;

      if (isNewLavorazione) {
        if (!newLavorazioneName.trim()) {
          showNotification('Inserisci il nome della nuova lavorazione', 'error');
          return;
        }
        lavorazioneName = newLavorazioneName.trim();
        lavorazioneId = `custom_${Date.now()}`;
        isCustom = true;
      } else {
        if (!selectedLavorazione) {
          showNotification('Seleziona una lavorazione', 'error');
          return;
        }
        const lavorazione = lavorazioni.find(l => l.id === selectedLavorazione);
        if (!lavorazione) {
          showNotification('Lavorazione non trovata', 'error');
          return;
        }
        lavorazioneName = lavorazione.nomeLavorazione || lavorazione.nome;
        lavorazioneId = selectedLavorazione;
        isCustom = false;
      }

      const lavorazioniPresenti = formData.lavorazioni || [];
      const esisteGia = lavorazioniPresenti.some(l => 
        l.lavorazioneId === lavorazioneId || 
        (isCustom && l.nomeLavorazione === lavorazioneName)
      );
      if (esisteGia) {
        showNotification('Questa lavorazione è già stata aggiunta', 'error');
        return;
      }

      const newEntry = {
        lavorazioneId: lavorazioneId,
        nomeLavorazione: lavorazioneName,
        descrizione: '',
        note: note.trim() || '',
        isCustom: isCustom
      };

      setFormData(prev => ({
        ...prev,
        lavorazioni: [...lavorazioniPresenti, newEntry]
      }));

      setSelectedLavorazione('');
      setNote('');
      setNewLavorazioneName('');
      setIsNewLavorazione(false);
    };

    const removeLavorazione = (index) => {
      const lavorazioniPresenti = formData.lavorazioni || [];
      setFormData(prev => ({
        ...prev,
        lavorazioni: lavorazioniPresenti.filter((_, i) => i !== index)
      }));
    };

    const updateNote = (index, newNote) => {
      const lavorazioniPresenti = formData.lavorazioni || [];
      const updatedLavorazioni = [...lavorazioniPresenti];
      updatedLavorazioni[index] = {
        ...updatedLavorazioni[index],
        note: newNote
      };
      
      setFormData(prev => ({
        ...prev,
        lavorazioni: updatedLavorazioni
      }));
    };

    const lavorazioniPresenti = formData.lavorazioni || [];

    return (
      <div className="form-section">
        
        <div className="lavorazioni-form">
          <div className="form-row">
            <div className="form-group full-width">
              <div className="toggle-section">
                <label>
                  <input
                    type="checkbox"
                    checked={isNewLavorazione}
                    onChange={(e) => {
                      setIsNewLavorazione(e.target.checked);
                      setSelectedLavorazione('');
                      setNewLavorazioneName('');
                    }}
                  />
                  Aggiungi nuova lavorazione personalizzata
                </label>
              </div>
            </div>

            {!isNewLavorazione ? (
              <div className="form-group">
                <LavorazioniAutocomplete
                  lavorazioni={lavorazioni}
                  selectedLavorazione={selectedLavorazione}
                  setSelectedLavorazione={setSelectedLavorazione}
                  onSelect={(lavorazione) => setSelectedLavorazione(lavorazione.id)}
                />
              </div>
            ) : (
              <div className="form-group">
                <label>Nome nuova lavorazione *</label>
                <input
                  type="text"
                  value={newLavorazioneName}
                  onChange={(e) => setNewLavorazioneName(e.target.value)}
                  placeholder="Es: Posa pavimentazione..."
                  className="form-control"
                />
              </div>
            )}
            
            <div className="form-group">
              <label>Note aggiuntive</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note specifiche sulla lavorazione..."
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <button 
                type="button" 
                onClick={addLavorazione}
                className="btn btn-primary"
                disabled={(!selectedLavorazione && !isNewLavorazione) || (isNewLavorazione && !newLavorazioneName.trim())}
              >
                ➕ Aggiungi
              </button>
            </div>
          </div>
        </div>
        
        {/* Lista lavorazioni aggiunte */}
        {lavorazioniPresenti.length > 0 && (
          <div className="lavorazioni-list">
            <table className="table">
              <thead>
                <tr>
                  <th>Lavorazione</th>
                  <th>Note</th>
                  <th>Tipo</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {lavorazioniPresenti.map((lavorazione, index) => (
                  <tr key={index}>
                    <td>{lavorazione.nomeLavorazione || getLavorazioneName(lavorazione.lavorazioneId)}</td>
                    <td>
                      <input
                        type="text"
                        value={lavorazione.note || ''}
                        onChange={(e) => updateNote(index, e.target.value)}
                        placeholder="Aggiungi note..."
                        className="form-control table-input"
                      />
                    </td>
                    <td>
                      {lavorazione.isCustom ? (
                        <span className="badge badge-success">Personalizzata</span>
                      ) : (
                        <span className="badge badge-primary">Standard</span>
                      )}
                    </td>
                    <td>
                      <button 
                        type="button"
                        onClick={() => removeLavorazione(index)}
                        className="btn btn-danger btn-sm"
                        title="Rimuovi lavorazione"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Sezione Allegati Completa e Sicura
  const AllegatiSection = () => {
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);

    const handleFileUpload = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      console.log('🚀 Inizio processo upload:', files.length, 'file');
      setUploadingFiles(true);
      setUploadProgress(files.map(f => ({ name: f.name, progress: 0, status: 'waiting' })));
      
      try {
        // FASE 1: VALIDAZIONE PRELIMINARE
        console.log('🔍 Fase 1: Validazione file...');
        for (const file of files) {
          console.log(`📄 Validazione: ${file.name}`, {
            type: file.type,
            size: `${(file.size / 1024).toFixed(1)}KB`
          });
          
          // Validazione tipo
          const allowedTypes = [
            'image/jpeg', 'image/jpg', 'image/png', 
            'image/gif', 'image/webp', 'application/pdf'
          ];
          if (!allowedTypes.includes(file.type)) {
            throw new Error(
              `❌ ${file.name}: Tipo non supportato (${file.type})\n` +
              `✅ Accettati: JPG, PNG, GIF, WebP, PDF`
            );
          }
          
          // Validazione dimensione
          const maxSize = 10 * 1024 * 1024; // 10MB
          if (file.size > maxSize) {
            throw new Error(
              `❌ ${file.name}: File troppo grande\n` +
              `📏 Dimensione: ${(file.size / 1024 / 1024).toFixed(2)}MB\n` +
              `✅ Massimo: 10MB`
            );
          }
        }
        console.log('✅ Validazione completata');

        // FASE 2: UPLOAD SEQUENZIALE
        console.log('📤 Fase 2: Upload file...');
        const uploadedFiles = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          
          // Aggiorna progress
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { ...p, status: 'uploading', progress: 10 } : p
          ));
          
          try {
            console.log(`⏳ Upload ${i + 1}/${files.length}: ${file.name}`);
            
            // Genera nome file unico
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(2, 8);
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const baseName = formData.data?.replace(/[^a-zA-Z0-9]/g, '_') || 'rapportino';
            const finalFileName = `${baseName}_${timestamp}_${randomId}.${fileExtension}`;
            
            console.log(`📝 Nome generato: ${finalFileName}`);
            
            // Simula progress
            setUploadProgress(prev => prev.map((p, idx) => 
              idx === i ? { ...p, progress: 50 } : p
            ));
            
            // Upload effettivo
            const downloadURL = await uploadRapportinoFile(file, cantiere.id, finalFileName);
            
            if (!downloadURL) {
              throw new Error('❌ Upload fallito: URL non ricevuto');
            }
            
            console.log(`✅ Upload ${i + 1} completato:`, downloadURL.substring(0, 50) + '...');
            
            // Aggiorna progress
            setUploadProgress(prev => prev.map((p, idx) => 
              idx === i ? { ...p, status: 'completed', progress: 100 } : p
            ));
            
            // Crea oggetto allegato
            const allegatoObj = {
              nome: file.name,
              nomeFile: finalFileName,
              url: downloadURL,
              tipo: file.type,
              dimensione: file.size,
              uploadedAt: new Date().toISOString(),
              uploadedBy: currentUser?.uid || 'unknown'
            };
            
            uploadedFiles.push(allegatoObj);
            
            // Notifica progresso
            showNotification(
              `✅ ${i + 1}/${files.length} completato: ${file.name}`,
              'info'
            );
            
          } catch (fileError) {
            console.error(`❌ Errore upload ${file.name}:`, fileError);
            
            // Aggiorna progress con errore
            setUploadProgress(prev => prev.map((p, idx) => 
              idx === i ? { ...p, status: 'error', progress: 0 } : p
            ));
            
            throw new Error(`❌ Errore su "${file.name}": ${fileError.message}`);
          }
        }

        // FASE 3: AGGIORNAMENTO FORM
        console.log('💾 Fase 3: Aggiornamento form data...');
        const allegatiAttuali = formData.allegati || [];
        const nuoviAllegati = [...allegatiAttuali, ...uploadedFiles];
        
        setFormData(prev => ({
          ...prev,
          allegati: nuoviAllegati
        }));

        console.log('🎉 Processo completato con successo!');
        showNotification(
          `🎉 ${uploadedFiles.length} file caricati con successo!`,
          'success'
        );
        
      } catch (error) {
        console.error('💥 Errore nel processo upload:', error);
        showNotification(
          `💥 Errore upload: ${error.message}`,
          'error'
        );
      } finally {
        setUploadingFiles(false);
        setUploadProgress([]);
        e.target.value = ''; // Reset input
      }
    };

    const removeAllegato = (index) => {
      const allegatiAttuali = formData.allegati || [];
      const allegatoRimosso = allegatiAttuali[index];
      
      console.log('🗑️ Rimozione allegato:', allegatoRimosso?.nome);
      
      setFormData(prev => ({
        ...prev,
        allegati: allegatiAttuali.filter((_, i) => i !== index)
      }));
      
      showNotification('🗑️ Allegato rimosso', 'info');
    };

    const allegatiPresenti = formData.allegati || [];

    return (
      <div className="form-section">
        <h4>📎 Allegati</h4>
        
        {/* SEZIONE UPLOAD */}
        <div className="upload-section">
          <div className="upload-input-container">
            <input
              type="file"
              onChange={handleFileUpload}
              multiple
              accept="image/*,.pdf"
              disabled={uploadingFiles}
              className="form-control file-input"
              id="file-upload-rapportino"
            />
            <label htmlFor="file-upload-rapportino" className="upload-label">
              {uploadingFiles ? (
                <div className="uploading-indicator">
                  <div className="upload-spinner"></div>
                  <span>📤 Upload in corso...</span>
                </div>
              ) : (
                <div className="upload-prompt">
                  <span className="upload-icon">📁</span>
                  <span className="upload-text">Clicca per selezionare file</span>
                  <small className="upload-hint">
                    📷 Immagini (JPG, PNG, GIF, WebP) e 📄 PDF<br/>
                    📏 Massimo 10MB per file
                  </small>
                </div>
              )}
            </label>
          </div>
          
          {/* PROGRESS UPLOAD */}
          {uploadProgress.length > 0 && (
            <div className="upload-progress-container">
              <h5>📊 Progresso Upload:</h5>
              {uploadProgress.map((progress, index) => (
                <div key={index} className="progress-item">
                  <div className="progress-info">
                    <span className="progress-name">{progress.name}</span>
                    <span className={`progress-status status-${progress.status}`}>
                      {progress.status === 'waiting' && '⏳ In attesa...'}
                      {progress.status === 'uploading' && '📤 Caricamento...'}
                      {progress.status === 'completed' && '✅ Completato'}
                      {progress.status === 'error' && '❌ Errore'}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${progress.progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* LISTA ALLEGATI */}
        {allegatiPresenti.length > 0 && (
          <div className="allegati-list">
            <h5>📎 File allegati ({allegatiPresenti.length}):</h5>
            <div className="allegati-grid">
              {allegatiPresenti.map((allegato, index) => (
                <div key={index} className="allegato-card">
                  <div className="allegato-header">
                    <div className="allegato-icon">
                      {allegato.tipo?.startsWith('image/') ? '🖼️' : '📄'}
                    </div>
                    <div className="allegato-info">
                      <div className="allegato-nome" title={allegato.nome}>
                        {allegato.nome}
                      </div>
                      <div className="allegato-meta">
                        <span>{(allegato.dimensione / 1024).toFixed(1)} KB</span>
                        {allegato.uploadedAt && (
                          <span>
                            {new Date(allegato.uploadedAt).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="allegato-actions">
                    <a 
                      href={allegato.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-secondary"
                    >
                      👁️ Vedi
                    </a>
                    <button 
                      type="button"
                      onClick={() => removeAllegato(index)}
                      className="btn btn-danger btn-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {allegatiPresenti.length === 0 && (
          <div className="no-allegati">
            <p>📭 Nessun file allegato</p>
          </div>
        )}
      </div>
    );
  };

  // Sezioni del form rapido (SENZA RICAVI)
  const sections = [
    { id: 'informazioni-base', label: 'Informazioni Base', icon: '📋' },
    { id: 'personale', label: 'Selezione Personale', icon: '👥' },
    { id: 'mezzi', label: 'Mezzi Utilizzati', icon: '🚛' },
    { id: 'materiali-variabili', label: 'Materiali Variabili', icon: '📦' },
    { id: 'materiali-fissi', label: 'Materiali Fissi', icon: '🔧' },
    { id: 'lavorazioni', label: 'Lavorazioni Eseguite', icon: '⚡' },
    // 🔴 RIMUOVI COMPLETAMENTE: { id: 'ricavi', label: 'Ricavi', icon: '💰' },
  ];

  // 🆕 NUOVO: Componente per mostrare lo stato distribuzione (versione responsabile)
const DistributionStatusIndicator = ({ rapportino }) => {
  if (!rapportino) return null;
  
  const hasDistributedHours = rapportino.personalePresente?.some(p => p.distributedHours === true);
  const distributionMetadata = rapportino.distributionMetadata;
  
  if (!hasDistributedHours && !distributionMetadata) return null;
};

  // Render delle sezioni del form rapido
  const renderQuickFormSection = () => {
    if (!selectedRapportino) {
      return (
        <div className="quick-form-empty">
          <h3>Nessun rapportino selezionato</h3>
          <p>Seleziona un rapportino dalla lista per visualizzarne i dettagli</p>
        </div>
      );
    }

    switch (selectedSection) {
      case 'informazioni-base':
        return (
          <div className="quick-form-section">
            <h3>📋 Informazioni Base</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Data:</label>
                <input 
                  type="date" 
                  value={formData.data || ''} 
                  onChange={(e) => setFormData(prev => ({...prev, data: e.target.value}))}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Creato da:</label>
                <span className="form-value">{getUserName(formData.redattoDa) || 'Non specificato'}</span>
              </div>
              {selectedRapportino && selectedRapportino.modificatoDa && (
                <div className="form-group">
                  <label>Modificato da:</label>
                  <span className="form-value">{getUserName(selectedRapportino.modificatoDa) || 'Non specificato'}</span>
                </div>
              )}
              {selectedRapportino && selectedRapportino.dataModifica && (
                <div className="form-group">
                  <label>Data modifica:</label>
                  <span className="form-value">{formatRapportinoDate(selectedRapportino.dataModifica)}</span>
                </div>
              )}
            </div>
            <div className="form-actions">
              <button 
                onClick={() => handleQuickUpdate('data', formData.data)}
                disabled={isUpdating || !formData.data}
                className="btn btn-primary"
              >
                {isUpdating ? 'Aggiornamento...' : 'Aggiorna Data'}
              </button>
            </div>
          </div>
        );

      case 'personale':
        return (
          <div className="quick-form-section">
            <h3>👥 Selezione Personale</h3>
            
            <AutoPersonaleSectionResponsabili 
              users={users}
              formData={formData}
              setFormData={setFormData}
              rapportinoDate={formData.data}
              editingRapportino={selectedRapportino}
              onRapportinoUpdate={loadRapportini}
            />
            
            {/* Pulsante per aggiornare dopo le modifiche */}
            <div className="form-actions">
              <button 
                onClick={() => handleQuickUpdate('personalePresente', formData.personalePresente)}
                disabled={isUpdating}
                className="btn btn-primary"
              >
                {isUpdating ? 'Aggiornamento...' : 'Aggiorna Personale'}
              </button>
            </div>
          </div>
        );

      case 'mezzi':
        return (
<div className="quick-form-section">
  {/* 🆕 SEZIONE HEADER CON PULSANTE COPIA */}
  <div className="section-header-with-actions">
    <h3>🚛 Mezzi Utilizzati</h3>
    <button
      type="button"
      onClick={handleOpenCopyMezziModal}
      className="btn btn-secondary btn-copy-mezzi"
      title="Copia mezzi e ore da un rapportino precedente"
    >
      📋 Copia da giorno precedente
    </button>
  </div>
  
  {/* Ricerca con suggerimenti sempre in cima */}
  <div className="search-section">
              <SearchWithSuggestions
                placeholder="🔍 Cerca mezzo per nome o targa..."
                value={searchMezzi}
                onChange={setSearchMezzi}
                suggestions={getAvailableMezzi()}
                onSelectSuggestion={handleSelectMezzo}
                getSuggestionText={(mezzo) => `${mezzo.nomeMezzo || mezzo.nome || ''} ${mezzo.targa || ''}`.trim()}
                renderSuggestion={(mezzo, searchTerm) => (
                  <div className="suggestion-content">
                    <div className="suggestion-name">
                      <strong>{mezzo.nomeMezzo || mezzo.nome}</strong>
                    </div>
                    <div className="suggestion-details">
                      {mezzo.targa && <span className="suggestion-targa">🚗 {mezzo.targa}</span>}
                      {/* 🔴 RIMUOVI: {mezzo.costoOrario && <span className="suggestion-cost">💰 €{mezzo.costoOrario}/h</span>} */}
                    </div>
                  </div>
                )}
                maxSuggestions={8}
              />
            </div>

 {/* Lista mezzi utilizzati (SENZA COSTI) */}
 <div className="items-list">
              {(formData.mezziUtilizzati || []).map((mezzo, index) => (
                <div key={index} className="list-item mezzo-item">
                  <div className="item-info">
                    <strong>{mezzo.nomeMezzo || getMezzoName(mezzo.mezzoId || mezzo.id)}</strong>
                    <span className="item-meta">{mezzo.targa ? `Targa: ${mezzo.targa}` : 'Nessuna targa'}</span>
                  </div>
                  <div className="item-controls">
  <label>Ore:</label>
  <input 
    type="number" 
    value={mezzo.ore || 0}
    onChange={(e) => {
      const newMezzi = [...formData.mezziUtilizzati];
      const oreValue = parseFloat(e.target.value) || 0;
      
      // 🔧 FIX: Trova il costo orario dal database se non presente
      let costoOrario = parseFloat(newMezzi[index].costoOrario || 0);
      if (costoOrario === 0) {
        const mezzoData = mezzi.find(m => m.id === (newMezzi[index].mezzoId || newMezzi[index].id));
        costoOrario = parseFloat(mezzoData?.costoOrario || 0);
      }
      
      const updatedMezzo = {
        ...mezzo, 
        ore: oreValue,
        costoOrario: costoOrario, // 🔧 FIX: Assicurati che sia presente
        totale: oreValue * costoOrario
      };
      newMezzi[index] = updatedMezzo;
      
      setFormData(prev => ({ ...prev, mezziUtilizzati: newMezzi }));
      
      clearTimeout(window.updateTimeout);
      window.updateTimeout = setTimeout(() => {
        handleQuickUpdate('mezziUtilizzati', newMezzi);
      }, 1000);
    }}
    min="0"
    step="0.5"
    className="input-small"
  />
  {/* 🔴 NON mostrare il costo ai responsabili, ma assicurati che sia calcolato */}
</div>
                  <button 
                    onClick={() => {
                      const newMezzi = formData.mezziUtilizzati.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, mezziUtilizzati: newMezzi }));
                      handleQuickUpdate('mezziUtilizzati', newMezzi);
                    }}
                    className="btn-remove"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              {(formData.mezziUtilizzati || []).length === 0 && (
                <div className="empty-list">
                  <p>Nessun mezzo aggiunto. Cerca e seleziona mezzi dal campo sopra.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'materiali-variabili':
        return (
          <div className="quick-form-section">
            <h3>📦 Materiali a Costo Variabile</h3>
            
            {/* Ricerca con suggerimenti sempre in cima */}
            <div className="search-section">
              <SearchWithSuggestions
                placeholder="🔍 Cerca materiale variabile..."
                value={searchMatVariabili}
                onChange={setSearchMatVariabili}
                suggestions={getAvailableMaterialiVariabili()}
                onSelectSuggestion={handleSelectMaterialeVariabile}
                getSuggestionText={(materiale) => materiale.nomeMacrovoce || materiale.nome || ''}
                renderSuggestion={(materiale, searchTerm) => (
                  <div className="suggestion-content">
                    <div className="suggestion-name">
                      <strong>{materiale.nomeMacrovoce || materiale.nome}</strong>
                    </div>
                    <div className="suggestion-details">
                      <span className="suggestion-type">📦 Costo Variabile</span>
                    </div>
                  </div>
                )}
                maxSuggestions={8}
              />
            </div>

            {/* Lista materiali variabili (SENZA COSTI) */}
            <div className="items-list">
              {(formData.materialiVariabili || []).map((materiale, index) => (
                <div key={index} className="list-item materiale-item">
                  <div className="item-info">
                    <strong>{materiale.nomeMacrovoce || getMaterialeVariabileName(materiale.materialeId || materiale.id)}</strong>
                  </div>
                  <div className="item-controls vertical">
                    <div className="control-group">
                      <label>Descrizione:</label>
                      <input 
                        type="text" 
                        value={materiale.descrizione || ''}
                        onChange={(e) => {
                          const newMateriali = [...formData.materialiVariabili];
                          newMateriali[index] = {...materiale, descrizione: e.target.value};
                          
                          setFormData(prev => ({ ...prev, materialiVariabili: newMateriali }));
                          
                          clearTimeout(window.descTimeout);
                          window.descTimeout = setTimeout(() => {
                            handleQuickUpdate('materialiVariabili', newMateriali);
                          }, 1500);
                        }}
                        placeholder="Descrizione facoltativa..."
                        className="input-medium"
                      />
                    </div>
                    {/* 🔴 RIMUOVI COMPLETAMENTE IL CONTROLLO COSTO */}
                  </div>
                  <button 
                    onClick={() => {
                      const newMateriali = formData.materialiVariabili.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, materialiVariabili: newMateriali }));
                      handleQuickUpdate('materialiVariabili', newMateriali);
                    }}
                    className="btn-remove"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              {(formData.materialiVariabili || []).length === 0 && (
                <div className="empty-list">
                  <p>Nessun materiale aggiunto. Cerca e seleziona materiali dal campo sopra.</p>
                </div>
              )}
            </div>
          </div>
        );
        case 'materiali-fissi':
          return (
            <div className="quick-form-section">
              <h3>🔧 Materiali a Costo Fisso</h3>
              
              {/* Ricerca con suggerimenti sempre in cima */}
              <div className="search-section">
                <SearchWithSuggestions
                  placeholder="🔍 Cerca materiale fisso..."
                  value={searchMatFissi}
                  onChange={setSearchMatFissi}
                  suggestions={getAvailableMaterialiFissi()}
                  onSelectSuggestion={handleSelectMaterialeFisso}
                  getSuggestionText={(materiale) => `${materiale.nomeMateriale || materiale.nome || ''} ${materiale.unitaMisura || ''}`.trim()}
                  renderSuggestion={(materiale, searchTerm) => (
                    <div className="suggestion-content">
                      <div className="suggestion-name">
                        <strong>{materiale.nomeMateriale || materiale.nome}</strong>
                      </div>
                      <div className="suggestion-details">
                        <span className="suggestion-unit">📏 {materiale.unitaMisura || 'pz'}</span>
                        {/* 🔴 RIMUOVI: {materiale.prezzoUnitario && <span className="suggestion-price">💰 €{materiale.prezzoUnitario}</span>} */}
                      </div>
                    </div>
                  )}
                  maxSuggestions={8}
                />
              </div>
  
           {/* Lista materiali fissi (SENZA COSTI) */}
           <div className="items-list">
              {(formData.materialiFissi || []).map((materiale, index) => (
                <div key={index} className="list-item materiale-item">
                  <div className="item-info">
                    <strong>{materiale.nomeMateriale || getMaterialeFissoName(materiale.materialeId || materiale.id)}</strong>
                    <span className="item-meta">{materiale.unitaMisura || 'pz'}</span>
                  </div>
                  <div className="item-controls">
  <label>Quantità:</label>
  <input 
    type="number" 
    value={materiale.quantita || 0}
    onChange={(e) => {
      const newMateriali = [...formData.materialiFissi];
      const quantitaValue = parseFloat(e.target.value) || 0;
      
      // 🔧 FIX: Trova il prezzo unitario dal database se non presente
      let prezzoUnitario = parseFloat(newMateriali[index].prezzoUnitario || 0);
      if (prezzoUnitario === 0) {
        const materialeData = materialiFissi.find(m => m.id === (newMateriali[index].materialeId || newMateriali[index].id));
        if (materialeData) {
          const prezzoBase = parseFloat(materialeData.prezzoUnitario || 0);
          const costoTrasporto = parseFloat(cantiere.variazioneCostiMateriali || 0);
          prezzoUnitario = prezzoBase + costoTrasporto;
        }
      }
      
      const updatedMateriale = {
        ...materiale, 
        quantita: quantitaValue,
        prezzoUnitario: prezzoUnitario, // 🔧 FIX: Assicurati che sia presente
        totale: quantitaValue * prezzoUnitario
      };
      newMateriali[index] = updatedMateriale;
      
      setFormData(prev => ({ ...prev, materialiFissi: newMateriali }));
      
      clearTimeout(window.quantitaTimeout);
      window.quantitaTimeout = setTimeout(() => {
        handleQuickUpdate('materialiFissi', newMateriali);
      }, 1000);
    }}
    min="0"
    step="0.01"
    className="input-small"
  />
  {/* 🔴 NON mostrare il costo ai responsabili, ma assicurati che sia calcolato */}
</div>
                    <button 
                      onClick={() => {
                        const newMateriali = formData.materialiFissi.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, materialiFissi: newMateriali }));
                        handleQuickUpdate('materialiFissi', newMateriali);
                      }}
                      className="btn-remove"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
  
                {(formData.materialiFissi || []).length === 0 && (
                  <div className="empty-list">
                    <p>Nessun materiale aggiunto. Cerca e seleziona materiali dal campo sopra.</p>
                  </div>
                )}
              </div>
            </div>
          );
  
        case 'lavorazioni':
          return (
            <div className="quick-form-section">
              <h3>⚡ Lavorazioni Eseguite</h3>
  
              {/* Ricerca con suggerimenti sempre in cima */}
              <div className="search-section">
                <SearchWithSuggestions
                  placeholder="🔍 Cerca lavorazione..."
                  value={searchLavorazioni}
                  onChange={setSearchLavorazioni}
                  suggestions={getAvailableLavorazioni()}
                  onSelectSuggestion={handleSelectLavorazione}
                  getSuggestionText={(lavorazione) => lavorazione.nomeLavorazione || lavorazione.nome || ''}
                  renderSuggestion={(lavorazione, searchTerm) => (
                    <div className="suggestion-content">
                      <div className="suggestion-name">
                        <strong>{lavorazione.nomeLavorazione || lavorazione.nome}</strong>
                      </div>
                      <div className="suggestion-details">
                        {lavorazione.descrizione && <span className="suggestion-desc">{lavorazione.descrizione}</span>}
                      </div>
                    </div>
                  )}
                  maxSuggestions={8}
                />
  
                {/* Campo per lavorazione personalizzata */}
                <div className="custom-add-section">
                  <input 
                    type="text"
                    placeholder="➕ Oppure scrivi una nuova lavorazione personalizzata..."
                    className="custom-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const lavorazioniPresenti = formData.lavorazioni || [];
                        const newLavorazione = {
                          lavorazioneId: `custom_${Date.now()}`,
                          nomeLavorazione: e.target.value.trim(),
                          descrizione: '',
                          note: '',
                          isCustom: true
                        };
                        const newLavorazioni = [...lavorazioniPresenti, newLavorazione];
                        
                        setFormData(prev => ({ ...prev, lavorazioni: newLavorazioni }));
                        handleQuickUpdate('lavorazioni', newLavorazioni);
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
  
              {/* Lista lavorazioni */}
              <div className="items-list">
                {(formData.lavorazioni || []).map((lavorazione, index) => (
                  <div key={index} className="list-item lavorazione-item">
                    <div className="item-info">
                      <strong>{lavorazione.nomeLavorazione || getLavorazioneName(lavorazione.lavorazioneId || lavorazione.id)}</strong>
                      {lavorazione.isCustom && <span className="custom-badge">PERSONALIZZATA</span>}
                      {lavorazione.note && <span className="item-note">{lavorazione.note}</span>}
                    </div>
                    <button 
                      onClick={() => {
                        const newLavorazioni = formData.lavorazioni.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, lavorazioni: newLavorazioni }));
                        handleQuickUpdate('lavorazioni', newLavorazioni);
                      }}
                      className="btn-remove"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
  
                {(formData.lavorazioni || []).length === 0 && (
                  <div className="empty-list">
                    <p>Nessuna lavorazione aggiunta. Cerca lavorazioni o aggiungi personalizzate dai campi sopra.</p>
                  </div>
                )}
              </div>
            </div>
          );
  
        default:
          return <div>Sezione non trovata</div>;
      }
    };
  
    if (isLoading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Caricamento rapportini...</p>
        </div>
      );
    }
  
    return (
      <div className="rapportini-split-view">
        {/* Notifiche */}
        {notification.show && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
          </div>
        )}
  
        {/* Header */}
        <div className="split-view-header">
          <div className="header-left">
            <button className="btn-back" onClick={onBack}>
              ← Torna al Cantiere
            </button>
            <div className="header-title">
              <h2>📝 Rapportini - {cantiere.nomeCommessa}</h2>
              <p>{rapportini.length} rapportini trovati</p>
            </div>
          </div>
          <div className="header-actions">
            {/* 🆕 NUOVO: Controlli sincronizzazione per responsabili */}
            <div className="sync-controls">
              <button 
                className="btn btn-outline-primary"
                disabled={syncStatus === 'syncing'}
                title="Sincronizza tutti i rapportini con le ore di workHours"
              >
                {syncStatus === 'syncing' ? '🔄 Sincronizzazione...' : 
                 syncStatus === 'success' ? '✅ Sincronizzato' :
                 syncStatus === 'error' ? '❌ Errore' :
                 '🔄 Sincronizza Tutto'}
              </button>
            </div>
            
            <button 
              className="btn btn-primary"
              onClick={handleNewRapportino}
            >
              ➕ Nuovo Rapportino
            </button>
          </div>
        </div>
  
        {/* Split View Container */}
        <div className="split-view-container">
          {/* Lista Rapportini - Sinistra */}
          <div className="split-view-left">
            <div className="rapportini-list">
              <h3>Lista Rapportini</h3>
              
              {/* Campo di ricerca */}
              <div className="search-rapportini">
                <div className="search-input-container">
                  <input
                    type="text"
                    placeholder="🔍 Cerca rapportini per data, redattore, note..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-rapportini-input"
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="clear-search-btn"
                      title="Cancella ricerca"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <div className="search-results-info">
                    {filteredRapportini.length} di {rapportini.length} rapportini
                  </div>
                )}
              </div>
              
  {/* Lista rapportini filtrata */}
  {filteredRapportini.length === 0 ? (
        <div className="empty-state">
          {searchTerm ? (
            <div>
              <p>Nessun rapportino trovato per "{searchTerm}"</p>
              <button 
                onClick={() => setSearchTerm('')}
                className="btn btn-secondary btn-sm"
              >
                Cancella ricerca
              </button>
            </div>
          ) : (
            <p>Nessun rapportino trovato</p>
          )}
        </div>
      ) : (
        <div className="rapportini-items">
          {filteredRapportini.map((rapportino) => {
            if (!rapportino || !rapportino.id) {
              console.warn('Rapportino non valido:', rapportino);
              return null;
            }
            
            return (
              <div 
                key={rapportino.id}
                className={`rapportino-item ${selectedRapportino?.id === rapportino.id ? 'selected' : ''} ${openSettingsDropdown === rapportino.id ? 'settings-open' : ''}`}
                onClick={() => handleSelectRapportino(rapportino)}
              >
                <div className="rapportino-name">
                  {/* 🆕 WRAPPER CON FLEX PER DATA + BADGE */}
                  <div className="rapportino-header-wrapper">
                    <strong>{formatRapportinoDate(rapportino.data)}</strong>
                    {/* 🆕 BADGE SINCRONIZZAZIONE */}
                    <SyncStatusBadge rapportino={rapportino} />
                  </div>
                  
                  <span className="rapportino-date">
                    Creato: {formatRapportinoDate(rapportino.createdAt)}
                  </span>
                  <span className="rapportino-redattore">
                    Da: {getUserName(rapportino.redattoDa)}
                  </span>
                </div>
                <div className="rapportino-actions">
                  <button 
                    className="settings-btn"
                    onClick={(e) => handleOpenSettings(rapportino.id, e)}
                    title="Opzioni rapportino"
                  >
                    ⚙️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
  
          {/* Form Modifica Rapida - Destra */}
          <div className="split-view-right">
            <div className="quick-edit-form">
              {/* Menu Selezione Settore */}
              <div className="section-selector">
                <h3>Modifica Rapida</h3>
                <div className="section-tabs">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      className={`section-tab ${selectedSection === section.id ? 'active' : ''}`}
                      onClick={() => setSelectedSection(section.id)}
                    >
                      <span className="section-icon">{section.icon}</span>
                      <span className="section-label">{section.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Aggiungi questo DOPO la sezione che mostra le informazioni del rapportino selezionato */}
{selectedRapportino && (
  <DistributionStatusIndicator rapportino={selectedRapportino} />
)}
  
              {/* Contenuto Sezione Selezionata */}
              <div className="section-content">
                {renderQuickFormSection()}
              </div>
            </div>
          </div>
        </div>
  
{/* Modal Form Avanzato CON SEZIONI COLLASSABILI */}
{showForm && (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && resetForm()}>
            <div className="modal-content large-modal">
              <div className="modal-header">
                <h3>
                  {editingRapportino ? 
                    `✏️ Modifica Rapportino del ${formatRapportinoDate(editingRapportino.data)}` : 
                    '➕ Nuovo Rapportino'
                  }
                </h3>
                <button className="modal-close" onClick={resetForm}>✕</button>
              </div>

              <form onSubmit={handleFormSubmit} className="rapportino-form">
                <div className="modal-body">
                  {/* Controllo di sicurezza: Verifica formData prima del rendering */}
                  {!formData || typeof formData !== 'object' ? (
                    <div className="alert alert-danger">
                      <h4>Errore nel caricamento del form</h4>
                      <p>Si è verificato un errore nel caricamento dei dati. Ricarica la pagina e riprova.</p>
                      <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
                        Ricarica Pagina
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* NUOVO: Controlli sezioni su mobile */}
                      <SectionControls
                        onExpandAll={expandAllSections}
                        onCollapseAll={collapseAllSections}
                        totalSections={formSections.length}
                        collapsedCount={collapsedSections.size}
                      />

                      {/* MODIFICA: Informazioni Base - ora collassabile */}
                      <CollapsibleFormSection
                        id="informazioni-base"
                        title="Informazioni Base"
                        icon="📅"
                        priority="high"
                        isCollapsed={isSectionCollapsed('informazioni-base')}
                        onToggle={toggleSection}
                      >
                        <div className="form-row">
                          <div className="form-group">
                            <label>Data Rapportino *</label>
                            <input
                              type="date"
                              value={formData.data || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                              max={new Date().toISOString().split('T')[0]}
                              required
                              className="form-control"
                            />
                          </div>
                          <div className="form-group">
                            <label>Redatto da</label>
                            <input
                              type="text"
                              value={(() => {
                                if (currentUser?.nome && currentUser?.cognome) {
                                  return `${currentUser.nome} ${currentUser.cognome}`;
                                }
                                return currentUser?.email || 'Utente non identificato';
                              })()}
                              disabled
                              className="form-control"
                            />
                          </div>
                          {/* Aggiungi informazioni di modifica solo se esistono */}
                          {editingRapportino && (
                            <>
                              <div className="form-group">
                                <label>Ultima modifica</label>
                                <input
                                  type="text"
                                  value={(() => {
                                    if (editingRapportino.dataModifica) {
                                      return formatRapportinoDate(editingRapportino.dataModifica);
                                    }
                                    return 'Mai modificato';
                                  })()}
                                  disabled
                                  className="form-control"
                                />
                              </div>
                              <div className="form-group">
                                <label>Modificato da</label>
                                <input
                                  type="text"
                                  value={getUserName(editingRapportino.modificatoDa) || 'Non specificato'}
                                  disabled
                                  className="form-control"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </CollapsibleFormSection>

                      {/* Sezioni del Rapportino CON COLLAPSIBLE */}
                      <CollapsibleFormSection
                        id="personale-presente"
                        title="👷 Personale Presente"
                        icon="👥"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('personale-presente')}
                        onToggle={toggleSection}
                      >
                        <PersonaleSectionWrapper />
                      </CollapsibleFormSection>

                      <CollapsibleFormSection
                        id="mezzi-utilizzati"
                        title="🚛 Mezzi Utilizzati"
                        icon="🚛"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('mezzi-utilizzati')}
                        onToggle={toggleSection}
                      >
                        <MezziSection />
                      </CollapsibleFormSection>

                      <CollapsibleFormSection
                        id="materiali-variabili"
                        title="📦 Materiali a Costo Variabile"
                        icon="📦"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('materiali-variabili')}
                        onToggle={toggleSection}
                      >
                        <MaterialiVariabiliSection />
                      </CollapsibleFormSection>

                      <CollapsibleFormSection
                        id="materiali-fissi"
                        title="🏗️ Materiali a Costo Fisso"
                        icon="🏗️"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('materiali-fissi')}
                        onToggle={toggleSection}
                      >
                        <MaterialiFissiSection />
                      </CollapsibleFormSection>

                      <CollapsibleFormSection
                        id="lavorazioni"
                        title="⚒️ Lavorazioni"
                        icon="⚒️"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('lavorazioni')}
                        onToggle={toggleSection}
                      >
                        <LavorazioniSection />
                      </CollapsibleFormSection>

                      <CollapsibleFormSection
                        id="allegati"
                        title="📎 Allegati"
                        icon="📎"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('allegati')}
                        onToggle={toggleSection}
                      >
                        <AllegatiSection />
                      </CollapsibleFormSection>

                      {/* MODIFICA: Note Varie - ora collassabile */}
                      <CollapsibleFormSection
                        id="note-aggiuntive"
                        title="Note Varie"
                        icon="📝"
                        priority="low"
                        isCollapsed={isSectionCollapsed('note-aggiuntive')}
                        onToggle={toggleSection}
                      >
                        <div className="form-group">
                          <label>Note aggiuntive</label>
                          <textarea
                            value={formData.noteVarie || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, noteVarie: e.target.value }))}
                            placeholder="Inserisci eventuali note aggiuntive..."
                            rows="4"
                            className="form-control"
                          />
                        </div>
                      </CollapsibleFormSection>

                      {/* MODIFICA: Riepilogo Rapportino - SENZA COSTI per Responsabili */}
                      <CollapsibleFormSection
                        id="totali"
                        title="📊 Riepilogo Rapportino"
                        icon="📊"
                        priority="normal"
                        isCollapsed={isSectionCollapsed('totali')}
                        onToggle={toggleSection}
                      >
                        <div className="totali-grid">
                          <div className="totale-item">
                            <span>Persone coinvolte:</span>
                            <span><strong>{(formData.personalePresente || []).length}</strong></span>
                          </div>
                          <div className="totale-item">
                            <span>Mezzi utilizzati:</span>
                            <span><strong>{(formData.mezziUtilizzati || []).length}</strong></span>
                          </div>
                          <div className="totale-item">
                            <span>Materiali variabili:</span>
                            <span><strong>{(formData.materialiVariabili || []).length}</strong></span>
                          </div>
                          <div className="totale-item">
                            <span>Materiali fissi:</span>
                            <span><strong>{(formData.materialiFissi || []).length}</strong></span>
                          </div>
                          <div className="totale-item">
                            <span>Lavorazioni eseguite:</span>
                            <span><strong>{(formData.lavorazioni || []).length}</strong></span>
                          </div>
                          <div className="totale-item">
                            <span>Allegati:</span>
                            <span><strong>{(formData.allegati || []).length}</strong></span>
                          </div>
                        </div>
                      </CollapsibleFormSection>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Annulla
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={!formData || typeof formData !== 'object'}
                  >
                    {editingRapportino ? 'Aggiorna Rapportino' : 'Crea Rapportino'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
  
        {/* Settings Dropdown Portal */}
        {openSettingsDropdown && (
          <SettingsDropdownPortal
            top={dropdownPosition.top}
            left={dropdownPosition.left}
            isMobile={isMobile}
            onClose={() => setOpenSettingsDropdown(null)}
          >
            <button 
              onClick={handleModificaAvanzata}
              className="success"
            >
              <span>✏️</span>
              Modifica Avanzata
            </button>
  
            <button 
              onClick={() => {
                handleSyncRapportino(selectedRapportino);
                setOpenSettingsDropdown(null);
              }}
              className="info"
            >
              <span>🔄</span>
              Sincronizza Ore
            </button>

            <button 
      onClick={handleDuplicaRapportino}
      className="warning"
    >
      <span>📋</span>
      Duplica Rapportino
    </button>
            
            <button 
              onClick={handleEliminaRapportino}
              className="danger"
            >
              <span>🗑️</span>
              Elimina Rapportino
            </button>
          </SettingsDropdownPortal>
        )}

{/* 🆕 MODAL COPIA MEZZI - CON GIORNO PRECEDENTE O CALENDARIO */}
{showCopyMezziModal && (
  <div className="modal-overlay" onClick={() => setShowCopyMezziModal(false)}>
    <div className="modal-content copy-mezzi-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>📋 Copia Mezzi da Rapportino</h3>
        <button 
          className="modal-close" 
          onClick={() => setShowCopyMezziModal(false)}
          aria-label="Chiudi"
        >
          ×
        </button>
      </div>
      
      <div className="modal-body">
        {/* 🆕 OPZIONE RAPIDA: GIORNO PRECEDENTE */}
        <div className="quick-copy-section">
          <h4>⚡ Copia Rapida</h4>
          <p className="section-description">
            Copia automaticamente i mezzi dal giorno immediatamente precedente
          </p>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={handleCopyFromPreviousDay}
          >
            📅 Copia dal giorno precedente
          </button>
        </div>

        {/* DIVISORE */}
        <div className="modal-divider">
          <span>oppure</span>
        </div>

        {/* 🆕 OPZIONE MANUALE: SCEGLI DATA DAL CALENDARIO */}
        <div className="manual-copy-section">
          <h4>🗓️ Scegli Data dal Calendario</h4>
          <p className="section-description">
            Seleziona una data specifica e il sistema cercherà il rapportino corrispondente:
          </p>
          
          <div className="calendar-selection">
            <div className="form-group">
              <label>Seleziona Data *</label>
              <input
                type="date"
                value={selectedDateForCopy}
                onChange={(e) => setSelectedDateForCopy(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="form-control date-picker-large"
                placeholder="Scegli una data"
              />
              <small className="form-text text-muted">
                💡 Seleziona una data per cercare il rapportino con i mezzi da copiare
              </small>
            </div>

            {/* 🆕 PREVIEW DATE DISPONIBILI */}
            {availableDatesForCopy.length > 0 && (
              <div className="available-dates-info">
                <details>
                  <summary>
                    📊 Date con rapportini disponibili ({availableDatesForCopy.length})
                  </summary>
                  <div className="dates-preview-list">
                    {availableDatesForCopy.slice(0, 5).map((rapportino) => (
                      <div 
                        key={rapportino.id}
                        className="date-preview-item"
                        onClick={() => setSelectedDateForCopy(rapportino.data)}
                      >
                        <span className="preview-date">
                          {new Date(rapportino.data).toLocaleDateString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="preview-mezzi">
                          {rapportino.numeroMezzi} {rapportino.numeroMezzi === 1 ? 'mezzo' : 'mezzi'}
                        </span>
                      </div>
                    ))}
                    {availableDatesForCopy.length > 5 && (
                      <div className="date-preview-more">
                        ...e altre {availableDatesForCopy.length - 5} date
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {availableDatesForCopy.length === 0 && (
              <div className="alert alert-info">
                ℹ️ Non ci sono rapportini precedenti con mezzi da copiare
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="modal-footer">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={() => setShowCopyMezziModal(false)}
        >
          Annulla
        </button>
        <button 
          type="button" 
          className="btn btn-success"
          onClick={handleCopyMezziFromDate}
          disabled={!selectedDateForCopy}
        >
          ✅ Copia Mezzi dalla Data Selezionata
        </button>
      </div>
    </div>
  </div>
)}

{/* 🆕 MODAL SELEZIONE DATA */}
<DatePickerModal
      isOpen={showDateModal}
      onClose={handleDateCancel}
      onConfirm={handleDateConfirm}
      originalDate={rapportinoToDuplicate?.data}
      title="Seleziona Data Rapportino Duplicato"
    />
    </div>
    );
  };
  
  export default ResponsabileRapportiniManagement;